import React, { useState, useEffect, useCallback } from 'react';
import { 
  Settings, Package, FolderOpen, Plus, Pencil, Trash2, Upload, 
  Save, ArrowLeft, Image as ImageIcon, X, FileText, Eye, LogOut,
  Phone, Mail, MessageSquare, Download, Clock, Users, Key, User,
  UserPlus, Building, Tag, Search, Filter, MoreVertical, ChevronDown, Move, Sparkles,
  MapPin, Briefcase, Calendar, CheckCircle2, Circle, GripVertical, Send, Check, DollarSign,
  ClipboardList, Copy, ExternalLink, Shield, Database, HardDrive, Loader2, UploadCloud, AlertTriangle, RefreshCw,
  Wifi, WifiOff, Lock, Play, ArrowRight, HelpCircle, Star, Menu, Grid, Minus, Cloud, CloudOff
} from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import AdminLogin from './AdminLogin';
import TasksSection from './TasksSection';
import NotificationsPanel from './NotificationsPanel';
import useWebSocket from '../hooks/useWebSocket';
import {
  getSettings, updateSettings, uploadLogo,
  getCategories, createCategory, updateCategory, deleteCategory, uploadCategoryImage,
  getProducts, createProduct, updateProduct, deleteProduct, uploadProductImage, 
  uploadProductAdditionalImage, deleteProductAdditionalImage, updateProductImageAdjustment,
  updateProductAdditionalImageAdjustment,
  getQuotes, getQuotesWithCustomers, getAdminUsers, deleteAdminUser, changePassword, adminRegister,
  getCrmContacts, createCrmContact, updateCrmContact, deleteCrmContact,
  addCrmNote, deleteCrmNote, importContactsFromQuotes, getCrmStats, getCrmTags,
  createCrmTag, deleteCrmTag, getCrmPipeline, createPipelineStatus, updatePipelineStatus,
  deletePipelineStatus, addCrmTask, updateCrmTask, deleteCrmTask,
  getAdminCustomers, deleteAdminCustomer,
  getPromoBanners, createPromoBanner, updatePromoBanner, deletePromoBanner, uploadPromoBannerImage,
  updateQuote, deleteQuote,
  getBackupInfo, exportDatabaseBackup, exportCompleteBackup, previewBackupRestore, restoreDatabaseBackup,
  getBackupConfig, updateBackupConfig, getBackupRecords, createManualBackup, downloadBackup, emailBackup, deleteBackup, runBackupNow,
  markContactAsContacted, updateUserPermissions,
  requestGelattiSync, getGelattiSyncStatus, getGelattiServerStatus,
  testGraphQLConnection, previewGraphQLProducts, syncGraphQLProducts, importSelectedGraphQLProducts, getGraphQLIntegrationStatus,
  getCevadorConfig, updateCevadorConfig, getAllCevadorModels, createCevadorModel, updateCevadorModel, deleteCevadorModel, uploadCevadorModelImage, reorderCevadorModels, getNextCevadorCode, getCevadorCategoryCounts,
  getDriveStatus, getDriveConfig, saveDriveConfig, connectDrive, disconnectDrive, uploadBackupToDrive
} from '../services/api';
import ImageEditor from './ImageEditor';
import CrmSection from './CrmSection';
import QuotesSection from './QuotesSection';
import TwoFactorSettings from './TwoFactorSettings';
import LandingSection from './admin/LandingSection';
import { Toaster } from './ui/toaster';
import {
  CategoryModal,
  ProductModal,
  NewUserModal,
  ContactModal,
  ContactDetailModal,
  BannerModal,
  ChangePasswordModal,
  QuoteDetailModal
} from './admin/modals';

// Database Backup Section Component
const DatabaseBackupSection = () => {
  // Tab state
  const [activeBackupTab, setActiveBackupTab] = useState('automatic'); // 'automatic', 'email', or 'drive'
  
  // Config state
  const [config, setConfig] = useState({
    enabled: false,
    interval_hours: 24,
    include_files: true,
    email_enabled: false,
    email_recipients: [],
    drive_enabled: false,
    last_backup_at: null,
    next_backup_at: null
  });
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  
  // Backup records state
  const [backupRecords, setBackupRecords] = useState([]);
  const [isLoadingRecords, setIsLoadingRecords] = useState(true);
  
  // Action states
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [emailingId, setEmailingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [uploadingToDriveId, setUploadingToDriveId] = useState(null);
  
  // Email input
  const [newEmail, setNewEmail] = useState('');
  
  // Google Drive state
  const [driveStatus, setDriveStatus] = useState({ configured: false, connected: false });
  const [isLoadingDrive, setIsLoadingDrive] = useState(true);
  const [isConnectingDrive, setIsConnectingDrive] = useState(false);
  const [driveClientId, setDriveClientId] = useState('');
  const [driveClientSecret, setDriveClientSecret] = useState('');
  const [isSavingDriveConfig, setIsSavingDriveConfig] = useState(false);

  // Load config, records, and drive status
  const loadData = async () => {
    setIsLoadingConfig(true);
    setIsLoadingRecords(true);
    setIsLoadingDrive(true);
    try {
      const [configData, recordsData, driveData, driveConfigData] = await Promise.all([
        getBackupConfig(),
        getBackupRecords(20),
        getDriveStatus().catch(() => ({ configured: false, connected: false })),
        getDriveConfig().catch(() => ({ configured: false }))
      ]);
      setConfig(configData);
      setBackupRecords(recordsData);
      setDriveStatus(driveData);
      if (driveConfigData.client_id) {
        setDriveClientId(driveConfigData.client_id);
      }
    } catch (error) {
      console.error('Erro ao carregar dados de backup:', error);
      toast.error('Erro ao carregar configurações de backup');
    } finally {
      setIsLoadingConfig(false);
      setIsLoadingRecords(false);
      setIsLoadingDrive(false);
    }
  };

  // Check for drive connection status in URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('drive_connected') === 'true') {
      toast.success('Google Drive conectado com sucesso!');
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      loadData();
    }
    if (urlParams.get('drive_error')) {
      toast.error(`Erro ao conectar Google Drive: ${urlParams.get('drive_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  // Save configuration
  const handleSaveConfig = async () => {
    setIsSavingConfig(true);
    try {
      const updated = await updateBackupConfig({
        enabled: config.enabled,
        interval_hours: config.interval_hours,
        include_files: config.include_files,
        email_enabled: config.email_enabled,
        email_recipients: config.email_recipients,
        drive_enabled: config.drive_enabled
      });
      setConfig(updated);
      toast.success('Configuração salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setIsSavingConfig(false);
    }
  };

  // Create manual backup
  const handleCreateBackup = async () => {
    setIsCreatingBackup(true);
    try {
      const result = await createManualBackup(config.include_files);
      toast.success('Backup criado com sucesso!');
      loadData(); // Reload records
    } catch (error) {
      console.error('Erro ao criar backup:', error);
      toast.error('Erro ao criar backup');
    } finally {
      setIsCreatingBackup(false);
    }
  };

  // Download backup
  const handleDownload = async (record) => {
    setDownloadingId(record.id);
    try {
      const response = await downloadBackup(record.filename);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', record.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar backup:', error);
      toast.error('Erro ao baixar backup');
    } finally {
      setDownloadingId(null);
    }
  };

  // Email backup
  const handleEmailBackup = async (record) => {
    if (!config.email_recipients || config.email_recipients.length === 0) {
      toast.error('Configure os destinatários de email primeiro');
      return;
    }
    setEmailingId(record.id);
    try {
      const result = await emailBackup(record.id, config.email_recipients);
      if (result.sent_filename && result.sent_filename !== record.filename) {
        // A smaller version was sent
        toast.success(`Email enviado! (versão compacta: ${result.sent_size_mb}MB - sem imagens)`, {
          duration: 6000
        });
      } else {
        toast.success('Backup enviado por email!');
      }
      loadData(); // Reload to update emailed_at
    } catch (error) {
      console.error('Erro ao enviar backup:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao enviar backup por email';
      toast.error(errorMsg, { duration: 8000 });
    } finally {
      setEmailingId(null);
    }
  };

  // Delete backup
  const handleDelete = async (record) => {
    if (!window.confirm(`Deseja remover o backup "${record.filename}"?`)) return;
    setDeletingId(record.id);
    try {
      await deleteBackup(record.id);
      toast.success('Backup removido!');
      loadData();
    } catch (error) {
      console.error('Erro ao remover backup:', error);
      toast.error('Erro ao remover backup');
    } finally {
      setDeletingId(null);
    }
  };

  // Save Google Drive credentials
  const handleSaveDriveConfig = async () => {
    if (!driveClientId || !driveClientSecret) {
      toast.error('Preencha o Client ID e Client Secret');
      return;
    }
    if (!driveClientId.endsWith('.apps.googleusercontent.com')) {
      toast.error('Client ID inválido. Deve terminar com .apps.googleusercontent.com');
      return;
    }
    setIsSavingDriveConfig(true);
    try {
      await saveDriveConfig(driveClientId, driveClientSecret);
      toast.success('Credenciais salvas! Agora clique em "Conectar Google Drive"');
      setDriveClientSecret(''); // Clear secret after saving
      loadData();
    } catch (error) {
      console.error('Erro ao salvar credenciais:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao salvar credenciais';
      toast.error(errorMsg);
    } finally {
      setIsSavingDriveConfig(false);
    }
  };

  // Connect Google Drive
  const handleConnectDrive = async () => {
    setIsConnectingDrive(true);
    try {
      const result = await connectDrive();
      if (result.authorization_url) {
        window.location.href = result.authorization_url;
      }
    } catch (error) {
      console.error('Erro ao conectar Drive:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao conectar Google Drive';
      toast.error(errorMsg);
      setIsConnectingDrive(false);
    }
  };

  // Disconnect Google Drive
  const handleDisconnectDrive = async () => {
    if (!window.confirm('Deseja desconectar o Google Drive?')) return;
    try {
      await disconnectDrive();
      setDriveStatus({ configured: true, connected: false });
      toast.success('Google Drive desconectado');
    } catch (error) {
      console.error('Erro ao desconectar Drive:', error);
      toast.error('Erro ao desconectar Google Drive');
    }
  };

  // Upload backup to Google Drive
  const handleUploadToDrive = async (record) => {
    if (!driveStatus.connected) {
      toast.error('Conecte o Google Drive primeiro');
      setActiveBackupTab('drive');
      return;
    }
    setUploadingToDriveId(record.id);
    try {
      const result = await uploadBackupToDrive(record.id);
      toast.success('Backup enviado para o Google Drive!');
      loadData();
    } catch (error) {
      console.error('Erro ao enviar para Drive:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao enviar para Google Drive';
      toast.error(errorMsg);
    } finally {
      setUploadingToDriveId(null);
    }
  };

  // Add email recipient
  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Digite um email válido');
      return;
    }
    if (config.email_recipients.includes(newEmail)) {
      toast.error('Email já adicionado');
      return;
    }
    setConfig(prev => ({
      ...prev,
      email_recipients: [...prev.email_recipients, newEmail]
    }));
    setNewEmail('');
  };

  // Remove email recipient
  const handleRemoveEmail = (email) => {
    setConfig(prev => ({
      ...prev,
      email_recipients: prev.email_recipients.filter(e => e !== email)
    }));
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm mt-4 sm:mt-6">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Backup</h2>
          <p className="text-sm text-slate-500">Gerencie backups do sistema</p>
        </div>
        <Button
          onClick={handleCreateBackup}
          disabled={isCreatingBackup}
          className="bg-[#195C75] hover:bg-[#0F4A5C] text-white flex items-center gap-2"
        >
          {isCreatingBackup ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Criando...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Criar Backup
            </>
          )}
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex">
          <button
            onClick={() => setActiveBackupTab('automatic')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeBackupTab === 'automatic'
                ? 'border-[#195C75] text-[#195C75]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Clock className="w-4 h-4" />
            Backup Automático
          </button>
          <button
            onClick={() => setActiveBackupTab('email')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeBackupTab === 'email'
                ? 'border-[#195C75] text-[#195C75]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Mail className="w-4 h-4" />
            Configuração Email
          </button>
          <button
            onClick={() => setActiveBackupTab('drive')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeBackupTab === 'drive'
                ? 'border-[#195C75] text-[#195C75]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {driveStatus.connected ? (
              <Cloud className="w-4 h-4 text-green-500" />
            ) : (
              <CloudOff className="w-4 h-4" />
            )}
            Google Drive
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-4 sm:p-6">
        {activeBackupTab === 'automatic' && (
          <div className="space-y-6">
            {/* Automatic Backup Config */}
            <div className="bg-slate-50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-5 h-5 text-[#195C75]" />
                <h3 className="font-semibold text-slate-800">Backup Automático</h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">Configure o backup automático do sistema</p>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                {/* Enable Toggle */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      config.enabled ? 'bg-[#195C75]' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        config.enabled ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className={`text-sm font-medium ${config.enabled ? 'text-[#195C75]' : 'text-slate-500'}`}>
                    {config.enabled ? 'Ativado' : 'Desativado'}
                  </span>
                </div>

                {/* Interval */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-slate-600">Intervalo (horas)</label>
                  <input
                    type="number"
                    min="1"
                    max="168"
                    value={config.interval_hours}
                    onChange={(e) => setConfig(prev => ({ ...prev, interval_hours: parseInt(e.target.value) || 24 }))}
                    className="w-20 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#195C75] focus:border-transparent"
                  />
                </div>

                {/* Include Files */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.include_files}
                    onChange={(e) => setConfig(prev => ({ ...prev, include_files: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-[#195C75] focus:ring-[#195C75]"
                  />
                  <span className="text-sm text-slate-600">Incluir arquivos (imagens)</span>
                </label>

                {/* Auto upload to Drive */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.drive_enabled}
                    onChange={(e) => setConfig(prev => ({ ...prev, drive_enabled: e.target.checked }))}
                    disabled={!driveStatus.connected}
                    className="w-4 h-4 rounded border-slate-300 text-[#195C75] focus:ring-[#195C75] disabled:opacity-50"
                  />
                  <span className={`text-sm ${driveStatus.connected ? 'text-slate-600' : 'text-slate-400'}`}>
                    Enviar para Google Drive
                    {!driveStatus.connected && ' (conecte primeiro)'}
                  </span>
                </label>

                {/* Save Button */}
                <Button
                  onClick={handleSaveConfig}
                  disabled={isSavingConfig}
                  variant="outline"
                  className="border-[#195C75] text-[#195C75]"
                >
                  {isSavingConfig ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar Configuração'
                  )}
                </Button>
              </div>

              {/* Next Backup Info */}
              {config.enabled && config.next_backup_at && (
                <div className="bg-[#195C75]/10 text-[#195C75] rounded-lg p-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">
                    Próximo backup automático: {formatDate(config.next_backup_at)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {activeBackupTab === 'email' && (
          <div className="space-y-6">
            {/* Email Configuration */}
            <div className="bg-slate-50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-5 h-5 text-[#195C75]" />
                <h3 className="font-semibold text-slate-800">Configuração de Email</h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">Configure o envio automático de backups por email</p>

              {/* Enable Email */}
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setConfig(prev => ({ ...prev, email_enabled: !prev.email_enabled }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    config.email_enabled ? 'bg-[#195C75]' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      config.email_enabled ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className={`text-sm font-medium ${config.email_enabled ? 'text-[#195C75]' : 'text-slate-500'}`}>
                  {config.email_enabled ? 'Envio por email ativado' : 'Envio por email desativado'}
                </span>
              </div>

              {/* Email Recipients */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Destinatários</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                    placeholder="email@exemplo.com"
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#195C75] focus:border-transparent"
                  />
                  <Button onClick={handleAddEmail} variant="outline" className="border-[#195C75] text-[#195C75]">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {config.email_recipients.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {config.email_recipients.map((email) => (
                      <div
                        key={email}
                        className="flex items-center gap-1 bg-white border border-slate-200 rounded-full px-3 py-1 text-sm"
                      >
                        <span>{email}</span>
                        <button
                          onClick={() => handleRemoveEmail(email)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                onClick={handleSaveConfig}
                disabled={isSavingConfig}
                className="bg-[#195C75] hover:bg-[#0F4A5C] text-white"
              >
                {isSavingConfig ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Salvando...
                  </>
                ) : (
                  'Salvar Configuração'
                )}
              </Button>
            </div>
          </div>
        )}

        {activeBackupTab === 'drive' && (
          <div className="space-y-6">
            {/* Google Drive Configuration */}
            <div className="bg-slate-50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Cloud className="w-5 h-5 text-[#195C75]" />
                <h3 className="font-semibold text-slate-800">Google Drive</h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Conecte seu Google Drive para salvar backups na nuvem sem limite de tamanho
              </p>

              {isLoadingDrive ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Verificando conexão...</span>
                </div>
              ) : !driveStatus.configured ? (
                <div className="space-y-4">
                  {/* Configuration Form */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Settings className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-blue-800">Configure as credenciais do Google Drive</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Crie as credenciais em: <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline">console.cloud.google.com</a>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Client ID</label>
                      <Input
                        type="text"
                        value={driveClientId}
                        onChange={(e) => setDriveClientId(e.target.value)}
                        placeholder="xxxxx.apps.googleusercontent.com"
                        className="font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Client Secret</label>
                      <Input
                        type="password"
                        value={driveClientSecret}
                        onChange={(e) => setDriveClientSecret(e.target.value)}
                        placeholder="GOCSPX-xxxxx"
                        className="font-mono text-sm"
                      />
                    </div>
                    <Button
                      onClick={handleSaveDriveConfig}
                      disabled={isSavingDriveConfig || !driveClientId || !driveClientSecret}
                      className="bg-[#195C75] hover:bg-[#0F4A5C] text-white"
                    >
                      {isSavingDriveConfig ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Salvando...
                        </>
                      ) : (
                        'Salvar Credenciais'
                      )}
                    </Button>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                    <p className="font-medium mb-1">URI de Redirecionamento (cole no Google Console):</p>
                    <code className="bg-white px-2 py-1 rounded border text-[10px] break-all block">
                      {window.location.origin}/api/admin/drive/callback
                    </code>
                  </div>
                </div>
              ) : driveStatus.connected ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Conectado</p>
                        {driveStatus.email && (
                          <p className="text-sm text-green-600">{driveStatus.email}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleDisconnectDrive}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      Desconectar
                    </Button>
                  </div>
                  
                  <div className="text-sm text-slate-600">
                    <p>Os backups serão salvos na pasta <strong>"Gelatti Backups"</strong> do seu Drive.</p>
                    <p className="mt-1">Clique no ícone <Cloud className="w-4 h-4 inline" /> na lista de backups para enviar ao Drive.</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">Credenciais configuradas!</span>
                    </div>
                    {driveClientId && (
                      <p className="text-xs text-green-600 mt-1 font-mono truncate">{driveClientId}</p>
                    )}
                  </div>
                  
                  <p className="text-sm text-slate-600">
                    Clique no botão abaixo para conectar sua conta Google.
                  </p>
                  <Button
                    onClick={handleConnectDrive}
                    disabled={isConnectingDrive}
                    className="bg-[#4285F4] hover:bg-[#3367D6] text-white flex items-center gap-2"
                  >
                    {isConnectingDrive ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Conectando...
                      </>
                    ) : (
                      <>
                        <Cloud className="w-4 h-4" />
                        Conectar Google Drive
                      </>
                    )}
                  </Button>
                  
                  <button
                    onClick={() => setDriveStatus({ configured: false, connected: false })}
                    className="text-xs text-slate-500 underline"
                  >
                    Alterar credenciais
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Backup Records List */}
      <div className="border-t p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <HardDrive className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-800">Backups Disponíveis</h3>
          <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">
            {backupRecords.length}
          </span>
        </div>

        {isLoadingRecords ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        ) : backupRecords.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <HardDrive className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>Nenhum backup encontrado</p>
            <p className="text-sm">Clique em "Criar Backup" para criar seu primeiro backup</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-slate-500 border-b">
                  <th className="pb-3 font-medium">Data</th>
                  <th className="pb-3 font-medium">Tipo</th>
                  <th className="pb-3 font-medium">Tamanho</th>
                  <th className="pb-3 font-medium">Coleções</th>
                  <th className="pb-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {backupRecords.map((record) => (
                  <tr key={record.id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-700">{formatDate(record.created_at)}</span>
                      </div>
                    </td>
                    <td className="py-4">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        record.type === 'automatic'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {record.type === 'automatic' ? 'Automático' : 'Manual'}
                      </span>
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-700">{formatSize(record.size_bytes)}</span>
                        {record.size_bytes > 20 * 1024 * 1024 && (
                          <span className="text-xs text-amber-600" title="Backup maior que 20MB. Ao enviar por email, será enviada versão compacta sem imagens.">
                            ⚠️
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 text-sm text-slate-500">{record.collections_count} coleções</td>
                    <td className="py-4">
                      <div className="flex items-center justify-end gap-1">
                        {/* Email */}
                        <button
                          onClick={() => handleEmailBackup(record)}
                          disabled={emailingId === record.id}
                          className="p-2 text-slate-400 hover:text-[#195C75] hover:bg-slate-100 rounded-lg transition-colors"
                          title={record.size_bytes > 20 * 1024 * 1024 
                            ? "Enviar por email (versão compacta sem imagens)" 
                            : "Enviar por email"}
                        >
                          {emailingId === record.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Mail className="w-4 h-4" />
                          )}
                        </button>
                        
                        {/* Download */}
                        <button
                          onClick={() => handleDownload(record)}
                          disabled={downloadingId === record.id || !record.file_exists}
                          className="p-2 text-slate-400 hover:text-[#195C75] hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                          title={record.file_exists ? "Baixar" : "Arquivo não encontrado"}
                        >
                          {downloadingId === record.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </button>
                        
                        {/* Upload to Google Drive */}
                        <button
                          onClick={() => handleUploadToDrive(record)}
                          disabled={uploadingToDriveId === record.id || !record.file_exists}
                          className={`p-2 rounded-lg transition-colors ${
                            record.drive_file_id 
                              ? 'text-green-500 hover:text-green-600 hover:bg-green-50' 
                              : 'text-slate-400 hover:text-[#4285F4] hover:bg-blue-50'
                          } disabled:opacity-50`}
                          title={record.drive_file_id ? "Já está no Drive" : "Enviar para Google Drive"}
                        >
                          {uploadingToDriveId === record.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Cloud className="w-4 h-4" />
                          )}
                        </button>
                        
                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(record)}
                          disabled={deletingId === record.id}
                          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remover backup"
                        >
                          {deletingId === record.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Integration Section Component (GraphQL API)
const IntegrationSection = ({ onProductsUpdated }) => {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    try {
      const result = await testGraphQLConnection();
      setConnectionStatus(result);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      setConnectionStatus({ success: false, message: 'Erro ao testar conexão' });
      toast.error('Erro ao testar conexão');
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleLoadPreview = async () => {
    setIsLoadingPreview(true);
    setSelectedProducts([]);
    try {
      const result = await previewGraphQLProducts();
      setPreview(result);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleImportSelected = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Selecione pelo menos um produto');
      return;
    }
    
    if (!window.confirm(`Deseja importar ${selectedProducts.length} produto(s) selecionado(s)?`)) return;
    
    setIsImporting(true);
    setImportResult(null);
    try {
      const result = await importSelectedGraphQLProducts(selectedProducts);
      setImportResult(result);
      if (result.success) {
        toast.success(result.message);
        if (onProductsUpdated) onProductsUpdated();
        loadStats();
        setSelectedProducts([]);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Erro na importação');
    } finally {
      setIsImporting(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await getGraphQLIntegrationStatus();
      setStats(result);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleAllProducts = () => {
    if (!preview) return;
    const filteredProducts = preview.products.filter(p => 
      p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.id.toString().includes(searchTerm)
    );
    const allIds = filteredProducts.map(p => p.id);
    const allSelected = allIds.every(id => selectedProducts.includes(id));
    
    if (allSelected) {
      setSelectedProducts(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedProducts(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const filteredPreviewProducts = preview?.products?.filter(p => 
    p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.id.toString().includes(searchTerm)
  ) || [];

  useEffect(() => {
    loadStats();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Integração com API GraphQL</h2>
            <p className="text-sm text-slate-500 mt-1">
              Importe produtos do sistema Gelatti
            </p>
          </div>
          {stats && (
            <div className="text-right">
              <p className="text-2xl font-bold text-[#195C75]">{stats.graphql_products}</p>
              <p className="text-xs text-slate-500">produtos importados</p>
            </div>
          )}
        </div>

        {/* Connection Test */}
        <div className="p-4 bg-slate-50 rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-700">Status da Conexão</p>
              <p className="text-xs text-slate-500">appgelattiapi.azurewebsites.net</p>
            </div>
            <div className="flex items-center gap-3">
              {connectionStatus && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  connectionStatus.success 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {connectionStatus.success ? `Conectado (${connectionStatus.product_count} produtos)` : 'Erro'}
                </span>
              )}
              <Button
                onClick={handleTestConnection}
                disabled={isTestingConnection}
                variant="outline"
                className="border-[#195C75] text-[#195C75]"
              >
                {isTestingConnection ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Testando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Testar Conexão
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Load Products Button */}
        <Button
          onClick={handleLoadPreview}
          disabled={isLoadingPreview}
          className="w-full bg-[#195C75] hover:bg-[#0F4A5C] text-white"
        >
          {isLoadingPreview ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Carregando produtos...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Carregar Produtos da API
            </>
          )}
        </Button>
      </div>

      {/* Import Result */}
      {importResult && (
        <div className={`p-4 rounded-xl ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <h3 className={`font-semibold mb-2 ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
            {importResult.success ? 'Importação Concluída!' : 'Erro na Importação'}
          </h3>
          {importResult.success && (
            <div className="flex gap-6 text-center">
              <div>
                <p className="text-2xl font-bold text-green-600">{importResult.created}</p>
                <p className="text-xs text-slate-500">Criados</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{importResult.updated}</p>
                <p className="text-xs text-slate-500">Atualizados</p>
              </div>
            </div>
          )}
          {!importResult.success && (
            <p className="text-sm text-red-600">{importResult.message}</p>
          )}
        </div>
      )}

      {/* Products List with Checkboxes */}
      {preview && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-slate-800">Produtos Disponíveis</h3>
              <p className="text-sm text-slate-500">
                {preview.active} ativos de {preview.total} total
              </p>
            </div>
            <div className="flex items-center gap-3">
              {selectedProducts.length > 0 && (
                <span className="px-3 py-1 bg-[#195C75] text-white text-sm rounded-full">
                  {selectedProducts.length} selecionado(s)
                </span>
              )}
              <Button
                onClick={handleImportSelected}
                disabled={isImporting || selectedProducts.length === 0}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Importar Selecionados
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="mb-4">
            <Input
              placeholder="Buscar por ID ou descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>
          
          <div className="max-h-[500px] overflow-y-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={filteredPreviewProducts.length > 0 && filteredPreviewProducts.every(p => selectedProducts.includes(p.id))}
                      onChange={toggleAllProducts}
                      className="w-4 h-4 rounded border-slate-300 text-[#195C75] focus:ring-[#195C75]"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-slate-600 font-semibold">ID</th>
                  <th className="px-4 py-3 text-left text-slate-600 font-semibold">Descrição</th>
                </tr>
              </thead>
              <tbody>
                {filteredPreviewProducts.map((prod, idx) => (
                  <tr 
                    key={prod.id} 
                    className={`border-t cursor-pointer transition-colors ${
                      selectedProducts.includes(prod.id) 
                        ? 'bg-[#195C75]/5' 
                        : idx % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-100'
                    }`}
                    onClick={() => toggleProductSelection(prod.id)}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(prod.id)}
                        onChange={() => toggleProductSelection(prod.id)}
                        className="w-4 h-4 rounded border-slate-300 text-[#195C75] focus:ring-[#195C75]"
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-500">{prod.id}</td>
                    <td className="px-4 py-3 text-slate-800">{prod.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <p className="text-xs text-slate-500 mt-3 text-center">
            Mostrando {filteredPreviewProducts.length} de {preview.active} produtos ativos
          </p>
        </div>
      )}
    </div>
  );
};

// Cevador Section Component
const CevadorSection = ({ config, onConfigUpdate, onModelCreate, onModelUpdate, onModelDelete, onModelImageUpload, saving }) => {
  const [localConfig, setLocalConfig] = useState(config || {
    min_quantity: 10,
    description: '',
    prices: [
      { min_quantity: 10, max_quantity: 49, price: 4.00 },
      { min_quantity: 50, max_quantity: 99, price: 3.50 },
      { min_quantity: 100, max_quantity: null, price: 3.00 }
    ],
    categories: [],
    active: true
  });
  const [batchUpload, setBatchUpload] = useState({ category: null, images: [] }); // images: [{file, preview}]
  const [nextCode, setNextCode] = useState('001');
  const [newCategory, setNewCategory] = useState('');
  const [editingModel, setEditingModel] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null); // { index, value }
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('categorias');
  const fileInputRef = React.useRef(null);
  const batchFileInputRef = React.useRef(null);
  const [uploadingModelId, setUploadingModelId] = useState(null);
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  
  // Pagination state for models
  const [models, setModels] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 24, total: 0, total_pages: 0, has_more: false });
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  
  // Real categories from database (with counts)
  const [realCategories, setRealCategories] = useState([]);
  
  // Batch edit state
  const [selectedModels, setSelectedModels] = useState(new Set());
  const [isBatchEditing, setIsBatchEditing] = useState(false);
  const [batchEditCategories, setBatchEditCategories] = useState(new Set()); // Multiple categories to add

  // Load real categories from database
  const loadRealCategories = React.useCallback(async () => {
    try {
      const result = await getCevadorCategoryCounts();
      setRealCategories(result.categories || []);
    } catch (error) {
      console.error('Error loading real categories:', error);
    }
  }, []);

  // Load categories on mount
  React.useEffect(() => {
    loadRealCategories();
  }, [loadRealCategories]);

  // Load models with pagination
  const loadModels = React.useCallback(async (page = 1, category = 'all', append = false) => {
    setIsLoadingModels(true);
    try {
      const categoryParam = category === 'all' ? null : category;
      const result = await getAllCevadorModels(page, 24, categoryParam);
      
      if (append && page > 1) {
        setModels(prev => [...prev, ...result.models]);
      } else {
        setModels(result.models);
      }
      setPagination(result.pagination);
      setModelsLoaded(true);
    } catch (error) {
      console.error('Error loading cevador models:', error);
      toast.error('Erro ao carregar modelos');
    } finally {
      setIsLoadingModels(false);
    }
  }, []);

  // Load models when "modelos" tab is active for the first time
  React.useEffect(() => {
    if (activeTab === 'modelos' && !modelsLoaded) {
      loadModels(1, selectedCategory);
    }
  }, [activeTab, modelsLoaded, loadModels, selectedCategory]);

  // Reload when category filter changes
  React.useEffect(() => {
    if (modelsLoaded && activeTab === 'modelos') {
      loadModels(1, selectedCategory);
    }
  }, [selectedCategory]);

  // Fetch next available code on mount and after creating a model
  const fetchNextCode = React.useCallback(async () => {
    try {
      const result = await getNextCevadorCode();
      setNextCode(result.next_code);
    } catch (error) {
      console.error('Error fetching next code:', error);
    }
  }, []);

  React.useEffect(() => {
    fetchNextCode();
  }, [fetchNextCode]);

  React.useEffect(() => {
    if (config) {
      setLocalConfig(prev => ({
        ...prev,
        ...config,
        categories: config.categories || []
      }));
    }
  }, [config]);

  // Wrapper functions for model operations that also update local state
  const handleModelCreate = async (model) => {
    const newModel = await onModelCreate(model);
    // Reload first page to include new model
    await loadModels(1, selectedCategory);
    fetchNextCode();
    return newModel;
  };

  const handleModelUpdate = async (modelId, data) => {
    await onModelUpdate(modelId, data);
    setModels(prev => prev.map(m => m.id === modelId ? { ...m, ...data } : m));
  };

  const handleModelDelete = async (modelId) => {
    await onModelDelete(modelId);
    setModels(prev => prev.filter(m => m.id !== modelId));
  };

  const handleModelImageUploadLocal = async (modelId, file) => {
    await onModelImageUpload(modelId, file);
    // Reload to get updated image URL
    const result = await getAllCevadorModels(pagination.page, 24, selectedCategory === 'all' ? null : selectedCategory);
    setModels(result.models);
  };

  const handleLoadMore = () => {
    if (pagination.has_more && !isLoadingModels) {
      loadModels(pagination.page + 1, selectedCategory, true);
    }
  };

  const handlePriceChange = (index, field, value) => {
    setLocalConfig(prev => {
      const newPrices = [...prev.prices];
      newPrices[index] = { ...newPrices[index], [field]: value === '' ? null : (field === 'price' ? parseFloat(value) : parseInt(value)) };
      return { ...prev, prices: newPrices };
    });
  };

  const addPriceRange = () => {
    setLocalConfig(prev => ({
      ...prev,
      prices: [...prev.prices, { min_quantity: 0, max_quantity: null, price: 0 }]
    }));
  };

  const removePriceRange = (index) => {
    setLocalConfig(prev => ({
      ...prev,
      prices: prev.prices.filter((_, i) => i !== index)
    }));
  };

  const addCategory = () => {
    if (!newCategory.trim()) return;
    if (localConfig.categories?.includes(newCategory.trim())) {
      toast.error('Categoria já existe');
      return;
    }
    setLocalConfig(prev => ({
      ...prev,
      categories: [...(prev.categories || []), newCategory.trim()]
    }));
    setNewCategory('');
  };

  const removeCategory = (cat) => {
    setLocalConfig(prev => ({
      ...prev,
      categories: (prev.categories || []).filter(c => c !== cat)
    }));
  };

  const updateCategory = (oldName, newName) => {
    if (!newName.trim()) return;
    if (newName.trim() === oldName) {
      setEditingCategory(null);
      return;
    }
    if (localConfig.categories?.includes(newName.trim())) {
      toast.error('Essa categoria já existe');
      return;
    }
    
    // Update category name in config
    setLocalConfig(prev => ({
      ...prev,
      categories: (prev.categories || []).map(c => c === oldName ? newName.trim() : c)
    }));
    
    // Update category in all models that use this category (supporting both old and new format)
    models.forEach(model => {
      // Handle new categories array format
      if (model.categories && Array.isArray(model.categories) && model.categories.includes(oldName)) {
        const newCategories = model.categories.map(c => c === oldName ? newName.trim() : c);
        handleModelUpdate(model.id, { categories: newCategories });
      }
      // Handle old single category format
      else if (model.category === oldName) {
        handleModelUpdate(model.id, { categories: [newName.trim()] });
      }
    });
    
    setEditingCategory(null);
    toast.success('Categoria atualizada!');
  };

  const handleSaveConfig = () => {
    onConfigUpdate({
      min_quantity: localConfig.min_quantity,
      description: localConfig.description,
      prices: localConfig.prices,
      categories: localConfig.categories || [],
      active: localConfig.active
    });
  };

  // Batch upload functions
  const handleBatchImagesSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // Create preview URLs for all files
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setBatchUpload(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
  };

  const removeBatchImage = (index) => {
    setBatchUpload(prev => {
      const newImages = [...prev.images];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return { ...prev, images: newImages };
    });
  };

  const clearBatchUpload = () => {
    batchUpload.images.forEach(img => URL.revokeObjectURL(img.preview));
    setBatchUpload({ category: null, images: [] });
    if (batchFileInputRef.current) {
      batchFileInputRef.current.value = '';
    }
  };

  const handleCreateBatch = async () => {
    if (!batchUpload.category) {
      toast.error('Selecione uma categoria');
      return;
    }
    if (batchUpload.images.length === 0) {
      toast.error('Selecione pelo menos uma imagem');
      return;
    }
    
    setIsCreatingBatch(true);
    setBatchProgress({ current: 0, total: batchUpload.images.length });
    
    try {
      for (let i = 0; i < batchUpload.images.length; i++) {
        setBatchProgress({ current: i + 1, total: batchUpload.images.length });
        
        // Create the model
        const createdModel = await onModelCreate({ 
          category: batchUpload.category,
          categories: [batchUpload.category]
        });
        
        // Upload the image
        if (createdModel?.id) {
          await onModelImageUpload(createdModel.id, batchUpload.images[i].file);
        }
      }
      
      toast.success(`${batchUpload.images.length} modelos criados com sucesso!`);
      clearBatchUpload();
      // Reload models to show new ones
      await loadModels(1, selectedCategory);
      fetchNextCode();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao criar modelos');
    } finally {
      setIsCreatingBatch(false);
      setBatchProgress({ current: 0, total: 0 });
    }
  };

  const handleImageUpload = async (modelId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingModelId(modelId);
    try {
      await handleModelImageUploadLocal(modelId, file);
    } finally {
      setUploadingModelId(null);
    }
    e.target.value = '';
  };

  // Batch edit functions
  const toggleModelSelection = (modelId) => {
    setSelectedModels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  const selectAllModels = (modelsToSelect) => {
    const ids = modelsToSelect.map(m => m.id);
    setSelectedModels(new Set(ids));
  };

  const clearSelection = () => {
    setSelectedModels(new Set());
    setBatchEditCategories(new Set());
  };

  const toggleBatchCategory = (cat) => {
    setBatchEditCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cat)) {
        newSet.delete(cat);
      } else {
        newSet.add(cat);
      }
      return newSet;
    });
  };

  const handleBatchAddCategories = async () => {
    if (batchEditCategories.size === 0 || selectedModels.size === 0) return;
    
    setIsBatchEditing(true);
    try {
      const modelIds = Array.from(selectedModels);
      const categoriesToAdd = Array.from(batchEditCategories);
      
      for (const modelId of modelIds) {
        // Find the current model to get existing categories
        const currentModel = models.find(m => m.id === modelId);
        const existingCategories = currentModel?.categories || [];
        
        // Merge existing categories with new ones (no duplicates)
        const mergedCategories = [...new Set([...existingCategories, ...categoriesToAdd])];
        
        await handleModelUpdate(modelId, { categories: mergedCategories });
      }
      toast.success(`Categorias adicionadas a ${modelIds.length} modelo(s)!`);
      clearSelection();
    } catch (error) {
      toast.error('Erro ao atualizar modelos');
    } finally {
      setIsBatchEditing(false);
    }
  };

  const handleBatchRemoveCategories = async () => {
    if (batchEditCategories.size === 0 || selectedModels.size === 0) return;
    
    setIsBatchEditing(true);
    try {
      const modelIds = Array.from(selectedModels);
      const categoriesToRemove = Array.from(batchEditCategories);
      
      for (const modelId of modelIds) {
        // Find the current model to get existing categories
        const currentModel = models.find(m => m.id === modelId);
        const existingCategories = currentModel?.categories || [];
        
        // Remove selected categories
        const remainingCategories = existingCategories.filter(c => !categoriesToRemove.includes(c));
        
        await handleModelUpdate(modelId, { categories: remainingCategories.length > 0 ? remainingCategories : null });
      }
      toast.success(`Categorias removidas de ${modelIds.length} modelo(s)!`);
      clearSelection();
    } catch (error) {
      toast.error('Erro ao atualizar modelos');
    } finally {
      setIsBatchEditing(false);
    }
  };

  const handleBatchToggleActive = async (active) => {
    if (selectedModels.size === 0) return;
    
    setIsBatchEditing(true);
    try {
      const modelIds = Array.from(selectedModels);
      for (const modelId of modelIds) {
        await handleModelUpdate(modelId, { active });
      }
      toast.success(`${modelIds.length} modelo(s) ${active ? 'ativado(s)' : 'desativado(s)'}!`);
      clearSelection();
    } catch (error) {
      toast.error('Erro ao atualizar modelos');
    } finally {
      setIsBatchEditing(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedModels.size === 0) return;
    
    if (!window.confirm(`Deseja remover ${selectedModels.size} modelo(s)?`)) return;
    
    setIsBatchEditing(true);
    try {
      const modelIds = Array.from(selectedModels);
      for (const modelId of modelIds) {
        await handleModelDelete(modelId);
      }
      toast.success(`${modelIds.length} modelo(s) removido(s)!`);
      clearSelection();
    } catch (error) {
      toast.error('Erro ao remover modelos');
    } finally {
      setIsBatchEditing(false);
    }
  };

  const baseUrl = window.location.origin;
  const cevadorUrl = `${baseUrl}/cevador`;

  // Get ALL categories - merge config categories with real categories from database
  const allCategories = React.useMemo(() => {
    const configCats = localConfig.categories || [];
    const realCatNames = realCategories.map(c => c.name);
    // Merge and deduplicate, preserving order (config first, then real)
    const merged = [...configCats];
    for (const name of realCatNames) {
      if (!merged.includes(name)) {
        merged.push(name);
      }
    }
    return merged;
  }, [localConfig.categories, realCategories]);

  // Get category count from realCategories
  const getCategoryCount = (catName) => {
    const cat = realCategories.find(c => c.name === catName);
    return cat ? cat.count : 0;
  };
  
  // Helper to check if model has category
  const modelHasCategory = (model, cat) => {
    if (model.categories && Array.isArray(model.categories)) {
      return model.categories.includes(cat);
    }
    return model.category === cat;
  };
  
  // Helper to check if model has no categories
  const modelHasNoCategory = (model) => {
    if (model.categories && Array.isArray(model.categories)) {
      return model.categories.length === 0;
    }
    return !model.category;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Configurar Cevador</h2>
          <p className="text-slate-500">Configure a página de pedidos de cevadores personalizados</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/cevador/catalog-pdf`, '_blank')}
            data-testid="cevador-catalog-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Catálogo PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              navigator.clipboard.writeText(cevadorUrl);
              toast.success('Link copiado!');
            }}
          >
            <Copy className="w-4 h-4 mr-2" />
            Copiar Link
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(cevadorUrl, '_blank')}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Visualizar
          </Button>
        </div>
      </div>

      {/* Status Toggle */}
      <div className="bg-white rounded-xl p-6 shadow-sm border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-800">Status da Página</h3>
            <p className="text-sm text-slate-500">Ativar ou desativar a página de cevadores</p>
          </div>
          <button
            onClick={() => setLocalConfig(prev => ({ ...prev, active: !prev.active }))}
            className={`relative w-14 h-7 rounded-full transition-colors ${localConfig.active ? 'bg-[#195C75]' : 'bg-slate-300'}`}
          >
            <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${localConfig.active ? 'left-8' : 'left-1'}`} />
          </button>
        </div>

        {/* Configurações Gerais */}
        <div className="mt-4 pt-4 border-t grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Quantidade Mínima</label>
            <Input 
              type="number"
              value={localConfig.min_quantity || ''}
              onChange={(e) => setLocalConfig(prev => ({ ...prev, min_quantity: parseInt(e.target.value) || 0 }))}
              placeholder="10"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Descrição da Página</label>
            <Input
              value={localConfig.description || ''}
              onChange={(e) => setLocalConfig(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descrição que aparecerá no topo..."
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {/* Tab Headers - Blue background */}
        <div className="flex bg-[#195C75]">
          <button
            onClick={() => setActiveTab('categorias')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
              activeTab === 'categorias' 
                ? 'text-white bg-[#195C75]' 
                : 'text-white/70 hover:text-white hover:bg-[#195C75]/80'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Tag className="w-4 h-4" />
              <span>Categorias</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === 'categorias' ? 'bg-white/20 text-white' : 'bg-white/10 text-white/70'}`}>
                {allCategories.length}
              </span>
            </div>
            {activeTab === 'categorias' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('precos')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
              activeTab === 'precos' 
                ? 'text-white bg-[#195C75]' 
                : 'text-white/70 hover:text-white hover:bg-[#195C75]/80'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <DollarSign className="w-4 h-4" />
              <span>Preços</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === 'precos' ? 'bg-white/20 text-white' : 'bg-white/10 text-white/70'}`}>
                {(localConfig.prices || []).length}
              </span>
            </div>
            {activeTab === 'precos' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('modelos')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
              activeTab === 'modelos' 
                ? 'text-white bg-[#195C75]' 
                : 'text-white/70 hover:text-white hover:bg-[#195C75]/80'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Grid className="w-4 h-4" />
              <span>Modelos</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === 'modelos' ? 'bg-white/20 text-white' : 'bg-white/10 text-white/70'}`}>
                {models.length}
              </span>
            </div>
            {activeTab === 'modelos' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white" />
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* TAB: Categorias */}
          {activeTab === 'categorias' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800">Categorias dos Modelos</h3>
                  <p className="text-sm text-slate-500">Organize seus modelos em categorias</p>
                </div>
              </div>
              
              {/* Add Category */}
              <div className="flex gap-2">
                <Input
                  placeholder="Nova categoria..."
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                />
                <Button onClick={addCategory} className="bg-[#195C75] hover:bg-[#195C75]/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </div>
              
              {/* Categories List */}
              {allCategories.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <Tag className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">Nenhuma categoria criada</p>
                  <p className="text-sm text-slate-400 mt-1">Adicione categorias para organizar seus modelos</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Categoria</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-slate-600 w-32">Modelos</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-slate-600 w-32">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {allCategories.map((cat, idx) => {
                        const modelCount = getCategoryCount(cat);
                        const isInConfig = (localConfig.categories || []).includes(cat);
                        const configIndex = (localConfig.categories || []).indexOf(cat);
                        const isEditing = editingCategory?.index === configIndex && isInConfig;
                        return (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="px-4 py-3">
                              {isEditing ? (
                                <Input
                                  value={editingCategory.value}
                                  onChange={(e) => setEditingCategory({ index: configIndex, value: e.target.value })}
                                  onBlur={() => updateCategory(cat, editingCategory.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      updateCategory(cat, editingCategory.value);
                                    } else if (e.key === 'Escape') {
                                      setEditingCategory(null);
                                    }
                                  }}
                                  autoFocus
                                  className="h-8"
                                />
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-800">{cat}</span>
                                  {!isInConfig && (
                                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                      não salvo no config
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#195C75]/10 text-[#195C75]">
                                {modelCount} {modelCount === 1 ? 'modelo' : 'modelos'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {!isInConfig ? (
                                  <button
                                    onClick={() => {
                                      setLocalConfig(prev => ({
                                        ...prev,
                                        categories: [...(prev.categories || []), cat]
                                      }));
                                      toast.success(`Categoria "${cat}" adicionada ao config`);
                                    }}
                                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    title="Adicionar ao config"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <>
                                    {!isEditing && (
                                      <button
                                        onClick={() => setEditingCategory({ index: configIndex, value: cat })}
                                        className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                                        title="Editar categoria"
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => removeCategory(cat)}
                                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Remover do config"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB: Preços */}
          {activeTab === 'precos' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800">Tabela de Preços</h3>
                  <p className="text-sm text-slate-500">Configure os preços por faixa de quantidade</p>
                </div>
                <Button onClick={addPriceRange} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Faixa
                </Button>
              </div>
              
              {/* Price Ranges */}
              <div className="space-y-3">
                {localConfig.prices?.map((range, index) => (
                  <div key={index} className="flex items-end gap-3 p-4 bg-slate-50 rounded-lg border">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-slate-500 mb-1">Quantidade Mínima</label>
                      <Input
                        type="number"
                        value={range.min_quantity || ''}
                        onChange={(e) => handlePriceChange(index, 'min_quantity', e.target.value)}
                        placeholder="10"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-slate-500 mb-1">Quantidade Máxima</label>
                      <Input
                        type="number"
                        value={range.max_quantity === null ? '' : range.max_quantity}
                        onChange={(e) => handlePriceChange(index, 'max_quantity', e.target.value)}
                        placeholder="∞ (deixe vazio)"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-slate-500 mb-1">Preço Unitário (R$)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={range.price || ''}
                        onChange={(e) => handlePriceChange(index, 'price', e.target.value)}
                        placeholder="3.50"
                      />
                    </div>
                    <button
                      onClick={() => removePriceRange(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      disabled={localConfig.prices.length <= 1}
                      title="Remover faixa"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Price Preview */}
              <div className="mt-6 p-4 bg-[#195C75]/5 rounded-lg border border-[#195C75]/20">
                <h4 className="font-medium text-[#195C75] mb-3">Preview da Tabela</h4>
                <div className="flex flex-wrap gap-2">
                  {localConfig.prices?.map((range, index) => (
                    <div key={index} className="px-4 py-2 bg-white rounded-lg border text-center min-w-[100px]">
                      <div className="text-xs text-slate-500">
                        {range.max_quantity ? `${range.min_quantity}-${range.max_quantity}` : `${range.min_quantity}+`} un
                      </div>
                      <div className="text-lg font-bold text-[#195C75]">
                        R$ {(range.price || 0).toFixed(2).replace('.', ',')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB: Modelos */}
          {activeTab === 'modelos' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800">Modelos de Cevador</h3>
                  <p className="text-sm text-slate-500">Gerencie os modelos disponíveis para os clientes</p>
                </div>
              </div>
              
              {/* Batch Upload Form */}
              <div className="p-5 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border-2 border-dashed border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Adicionar Modelos em Lote
                    <span className="ml-2 px-2 py-0.5 bg-[#195C75]/10 text-[#195C75] rounded text-xs font-normal">
                      Próximo código: {nextCode}
                    </span>
                  </h4>
                  {batchUpload.images.length > 0 && (
                    <button
                      onClick={clearBatchUpload}
                      className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                    >
                      <X className="w-3 h-3" />
                      Limpar tudo
                    </button>
                  )}
                </div>
                
                <div className="space-y-4">
                  {/* Step 1: Select Images */}
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-2 block">
                      1. Selecione as imagens
                      {batchUpload.images.length > 0 && (
                        <span className="ml-2 text-green-600">({batchUpload.images.length} selecionada{batchUpload.images.length > 1 ? 's' : ''})</span>
                      )}
                    </label>
                    <input
                      type="file"
                      ref={batchFileInputRef}
                      accept="image/*"
                      multiple
                      onChange={handleBatchImagesSelect}
                      className="hidden"
                    />
                    
                    <div className="flex flex-wrap gap-3">
                      {/* Selected images preview */}
                      {batchUpload.images.map((img, index) => (
                        <div 
                          key={index}
                          className="relative w-20 h-20 rounded-lg overflow-hidden border-2 border-green-400 group"
                        >
                          <img 
                            src={img.preview} 
                            alt={`Imagem ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => removeBatchImage(index)}
                              className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                            >
                              <X className="w-3.5 h-3.5 text-white" />
                            </button>
                          </div>
                          <div className="absolute bottom-0.5 right-0.5 px-1 py-0.5 bg-black/70 rounded text-[9px] text-white font-mono">
                            {String(parseInt(nextCode) + index).padStart(3, '0')}
                          </div>
                        </div>
                      ))}
                      
                      {/* Add more button */}
                      <button
                        type="button"
                        onClick={() => batchFileInputRef.current?.click()}
                        className="w-20 h-20 bg-white rounded-lg border-2 border-dashed border-slate-300 hover:border-[#195C75] hover:bg-slate-50 transition-colors flex flex-col items-center justify-center"
                      >
                        <Upload className="w-6 h-6 text-slate-400" />
                        <span className="text-[10px] text-slate-400 mt-1">
                          {batchUpload.images.length === 0 ? 'Selecionar' : '+ Mais'}
                        </span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Step 2: Select Category */}
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-2 block">
                      2. Selecione a categoria
                      {batchUpload.category && (
                        <span className="ml-2 text-green-600">✓</span>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {allCategories.map(cat => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setBatchUpload(prev => ({ 
                            ...prev, 
                            category: prev.category === cat ? null : cat 
                          }))}
                          className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                            batchUpload.category === cat
                              ? 'bg-[#195C75] text-white shadow-md scale-105'
                              : 'bg-white border-2 border-slate-200 text-slate-600 hover:border-[#195C75] hover:text-[#195C75]'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                      {allCategories.length === 0 && (
                        <span className="text-sm text-slate-400 italic py-2">
                          Crie categorias na aba "Categorias" primeiro
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Preview and Create Button */}
                  {batchUpload.images.length > 0 && batchUpload.category && (
                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200">
                      <div>
                        <p className="text-sm font-medium text-slate-700">
                          Criar {batchUpload.images.length} modelo{batchUpload.images.length > 1 ? 's' : ''}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Nomes: {batchUpload.category} {nextCode}
                          {batchUpload.images.length > 1 && (
                            <span> até {batchUpload.category} {String(parseInt(nextCode) + batchUpload.images.length - 1).padStart(3, '0')}</span>
                          )}
                        </p>
                      </div>
                      <Button 
                        onClick={handleCreateBatch} 
                        disabled={isCreatingBatch}
                        className="bg-[#195C75] hover:bg-[#195C75]/90 h-12 px-8 text-base"
                      >
                        {isCreatingBatch ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Criando {batchProgress.current}/{batchProgress.total}...
                          </>
                        ) : (
                          <>
                            <Plus className="w-5 h-5 mr-2" />
                            Criar Todos
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {/* Help text when nothing selected */}
                  {batchUpload.images.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-2">
                      Selecione uma ou mais imagens, depois escolha a categoria. O nome será gerado automaticamente (ex: Diversos 007, Diversos 008...)
                    </p>
                  )}
                </div>
              </div>

              {/* Category Filter */}
              {allCategories.length > 0 && (
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === 'all' 
                        ? 'bg-[#195C75] text-white' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    Todos {selectedCategory === 'all' && pagination.total > 0 ? `(${pagination.total})` : ''}
                  </button>
                  {allCategories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedCategory === cat 
                          ? 'bg-[#195C75] text-white' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {cat} {selectedCategory === cat && pagination.total > 0 ? `(${pagination.total})` : ''}
                    </button>
                  ))}
                  <button
                    onClick={() => setSelectedCategory('uncategorized')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === 'uncategorized' 
                        ? 'bg-[#195C75] text-white' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    Sem categoria {selectedCategory === 'uncategorized' && pagination.total > 0 ? `(${pagination.total})` : ''}
                  </button>
                  {isLoadingModels && (
                    <Loader2 className="w-4 h-4 text-[#195C75] animate-spin ml-2" />
                  )}
                </div>
              )}

              {/* Batch Edit Actions Bar */}
              {selectedModels.size > 0 && (
                <div className="p-4 bg-[#195C75]/10 rounded-lg border border-[#195C75]/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-[#195C75]">
                        {selectedModels.size} modelo(s) selecionado(s)
                      </span>
                      <button
                        onClick={clearSelection}
                        className="text-xs text-slate-500 hover:text-slate-700"
                      >
                        Limpar seleção
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Toggle Active */}
                      <button
                        onClick={() => handleBatchToggleActive(true)}
                        disabled={isBatchEditing}
                        className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-xs font-medium transition-colors"
                      >
                        Ativar
                      </button>
                      <button
                        onClick={() => handleBatchToggleActive(false)}
                        disabled={isBatchEditing}
                        className="px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg text-xs font-medium transition-colors"
                      >
                        Desativar
                      </button>
                      <div className="w-px h-6 bg-slate-300" />
                      {/* Delete */}
                      <button
                        onClick={handleBatchDelete}
                        disabled={isBatchEditing}
                        className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-medium transition-colors"
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                  
                  {/* Category Management */}
                  <div className="flex items-center gap-4 pt-2 border-t border-[#195C75]/20">
                    <span className="text-xs font-medium text-slate-600">Categorias:</span>
                    <div className="flex items-center gap-2">
                      {allCategories.map(cat => (
                        <button
                          key={cat}
                          onClick={() => toggleBatchCategory(cat)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            batchEditCategories.has(cat)
                              ? 'bg-[#195C75] text-white ring-2 ring-[#195C75] ring-offset-1'
                              : 'bg-white border border-slate-200 text-slate-600 hover:border-[#195C75]'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                    {batchEditCategories.size > 0 && (
                      <div className="flex items-center gap-2 ml-2">
                        <Button
                          size="sm"
                          onClick={handleBatchAddCategories}
                          disabled={isBatchEditing}
                          className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                        >
                          {isBatchEditing ? <Loader2 className="w-3 h-3 animate-spin" /> : (
                            <>
                              <Plus className="w-3 h-3 mr-1" />
                              Adicionar
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleBatchRemoveCategories}
                          disabled={isBatchEditing}
                          className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                        >
                          {isBatchEditing ? <Loader2 className="w-3 h-3 animate-spin" /> : (
                            <>
                              <Minus className="w-3 h-3 mr-1" />
                              Remover
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Models Grid */}
              {isLoadingModels && models.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <Loader2 className="w-8 h-8 mx-auto mb-3 text-[#195C75] animate-spin" />
                  <p className="text-slate-500">Carregando modelos...</p>
                </div>
              ) : models.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <Grid className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">Nenhum modelo cadastrado</p>
                  <p className="text-sm text-slate-400 mt-1">Adicione modelos de cevador usando o formulário acima</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-center px-2 py-2 w-10">
                          <input
                            type="checkbox"
                            checked={selectedModels.size === models.length && models.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                selectAllModels(models);
                              } else {
                                clearSelection();
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-300 text-[#195C75] focus:ring-[#195C75]"
                          />
                        </th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-slate-600 w-16">Imagem</th>
                        <th className="text-center px-3 py-2 text-xs font-medium text-slate-600 w-16">Código</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Nome</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-slate-600 w-32">Categoria</th>
                        <th className="text-center px-3 py-2 text-xs font-medium text-slate-600 w-20">Status</th>
                        <th className="text-center px-3 py-2 text-xs font-medium text-slate-600 w-28">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {models.map((model) => (
                        <tr 
                          key={model.id} 
                          className={`hover:bg-slate-50 ${model.active === false ? 'opacity-50' : ''} ${selectedModels.has(model.id) ? 'bg-[#195C75]/5' : ''}`}
                        >
                          {/* Checkbox */}
                          <td className="px-2 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectedModels.has(model.id)}
                              onChange={() => toggleModelSelection(model.id)}
                              className="w-4 h-4 rounded border-slate-300 text-[#195C75] focus:ring-[#195C75]"
                            />
                          </td>
                          {/* Image */}
                          <td className="px-3 py-2">
                            <div className="w-12 h-12 bg-slate-100 rounded-lg overflow-hidden relative group">
                              {model.image_url ? (
                                <img src={model.image_url} alt={model.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-400">
                                  <ImageIcon className="w-5 h-5" />
                                </div>
                              )}
                              {uploadingModelId === model.id && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                </div>
                              )}
                              <label className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer">
                                <input 
                                  type="file" 
                                  accept="image/*" 
                                  onChange={(e) => handleImageUpload(model.id, e)}
                                  className="hidden"
                                />
                                <Upload className="w-4 h-4 text-white" />
                              </label>
                            </div>
                          </td>
                          
                          {/* Code */}
                          <td className="px-3 py-2 text-center">
                            <span className="inline-flex items-center justify-center px-2 py-1 bg-slate-100 rounded-md text-xs font-mono font-bold text-slate-700">
                              {model.code || '-'}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {editingModel?.id === model.id ? (
                              <Input
                                value={editingModel.name}
                                onChange={(e) => setEditingModel({ ...editingModel, name: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    // Save on Enter
                                    if (editingModel.name.trim()) {
                                      const categoriesChanged = JSON.stringify(editingModel.categories) !== JSON.stringify(model.categories || []);
                                      if (editingModel.name !== model.name || categoriesChanged) {
                                        onModelUpdate(model.id, { 
                                          name: editingModel.name.trim(), 
                                          categories: editingModel.categories.length > 0 ? editingModel.categories : null 
                                        });
                                      }
                                    }
                                    setEditingModel(null);
                                  }
                                  if (e.key === 'Escape') setEditingModel(null);
                                }}
                                autoFocus
                                className="h-8 text-sm"
                              />
                            ) : (
                              <span className="font-medium text-slate-800 text-sm">{model.name}</span>
                            )}
                          </td>
                          
                          {/* Categories */}
                          <td className="px-3 py-2">
                            {editingModel?.id === model.id ? (
                              <div className="flex flex-wrap gap-1">
                                {allCategories.map(cat => (
                                  <button
                                    key={cat}
                                    type="button"
                                    onClick={() => {
                                      setEditingModel(prev => ({
                                        ...prev,
                                        categories: prev.categories.includes(cat)
                                          ? prev.categories.filter(c => c !== cat)
                                          : [...prev.categories, cat]
                                      }));
                                    }}
                                    className={`px-1.5 py-0.5 rounded text-xs transition-colors ${
                                      editingModel.categories.includes(cat)
                                        ? 'bg-[#195C75] text-white'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                  >
                                    {cat}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {(model.categories && model.categories.length > 0) ? (
                                  model.categories.map(cat => (
                                    <span key={cat} className="inline-block text-xs px-2 py-0.5 bg-[#195C75]/10 text-[#195C75] rounded-full">
                                      {cat}
                                    </span>
                                  ))
                                ) : model.category ? (
                                  <span className="inline-block text-xs px-2 py-0.5 bg-[#195C75]/10 text-[#195C75] rounded-full">
                                    {model.category}
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400">-</span>
                                )}
                              </div>
                            )}
                          </td>
                          
                          {/* Status */}
                          <td className="px-3 py-2 text-center">
                            {model.active !== false ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                                Ativo
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>
                                Inativo
                              </span>
                            )}
                          </td>
                          
                          {/* Actions */}
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-center gap-1">
                              {editingModel?.id === model.id ? (
                                <>
                                  <button
                                    onClick={() => {
                                      if (editingModel.name.trim()) {
                                        const categoriesChanged = JSON.stringify(editingModel.categories) !== JSON.stringify(model.categories || []);
                                        if (editingModel.name !== model.name || categoriesChanged) {
                                          onModelUpdate(model.id, { 
                                            name: editingModel.name.trim(), 
                                            categories: editingModel.categories.length > 0 ? editingModel.categories : null 
                                          });
                                        }
                                      }
                                      setEditingModel(null);
                                    }}
                                    className="p-1.5 bg-green-100 hover:bg-green-200 rounded transition-colors"
                                    title="Salvar"
                                  >
                                    <Check className="w-3.5 h-3.5 text-green-600" />
                                  </button>
                                  <button
                                    onClick={() => setEditingModel(null)}
                                    className="p-1.5 bg-red-100 hover:bg-red-200 rounded transition-colors"
                                    title="Cancelar"
                                  >
                                    <X className="w-3.5 h-3.5 text-red-600" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => setEditingModel({ 
                                      id: model.id, 
                                      name: model.name, 
                                      categories: model.categories || (model.category ? [model.category] : [])
                                    })}
                                    className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                                    title="Editar"
                                  >
                                    <Pencil className="w-3.5 h-3.5 text-slate-500" />
                                  </button>
                                  <button
                                    onClick={() => onModelUpdate(model.id, { active: !model.active })}
                                    className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                                    title={model.active !== false ? 'Desativar' : 'Ativar'}
                                  >
                                    <Eye className={`w-3.5 h-3.5 ${model.active !== false ? 'text-green-500' : 'text-slate-400'}`} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (window.confirm(`Remover o modelo "${model.name}"?`)) {
                                        onModelDelete(model.id);
                                      }
                                    }}
                                    className="p-1.5 hover:bg-red-50 rounded transition-colors"
                                    title="Remover"
                                  >
                                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Pagination Info and Load More */}
                  <div className="p-4 bg-slate-50 border-t flex items-center justify-between">
                    <p className="text-sm text-slate-500">
                      Mostrando {models.length} de {pagination.total} modelos
                    </p>
                    {pagination.has_more && (
                      <Button
                        onClick={handleLoadMore}
                        disabled={isLoadingModels}
                        variant="outline"
                        className="border-[#195C75] text-[#195C75]"
                      >
                        {isLoadingModels ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Carregando...
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-2" />
                            Carregar Mais ({pagination.total - models.length} restantes)
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="px-6 py-4 border-t bg-slate-50 flex justify-end">
          <Button 
            onClick={handleSaveConfig}
            disabled={saving}
            size="sm"
            className="bg-[#195C75] hover:bg-[#195C75]/90"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Gelatti Section Component
const GelattiSection = ({ onProductsUpdated }) => {
  const [serverStatus, setServerStatus] = useState({ online: false });
  const [syncStatus, setSyncStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState('sync'); // sync, config, logs
  const [syncMode, setSyncMode] = useState('incremental'); // incremental, completo

  const checkServerStatus = async () => {
    try {
      const status = await getGelattiServerStatus();
      setServerStatus(status);
    } catch (error) {
      setServerStatus({ online: false });
    }
  };

  const checkSyncStatus = async () => {
    try {
      const status = await getGelattiSyncStatus();
      setSyncStatus(status);
      return status;
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      return null;
    }
  };

  const handleRequestSync = async () => {
    if (!serverStatus.online) {
      toast.error('Servidor local não está conectado!');
      return;
    }
    
    setIsLoading(true);
    try {
      await requestGelattiSync();
      toast.success('Sincronização solicitada!');
      
      // Poll for completion
      const interval = setInterval(async () => {
        const status = await checkSyncStatus();
        if (status && !status.pending) {
          clearInterval(interval);
          setIsLoading(false);
          
          if (status.status === 'completed') {
            const result = status.last_result || {};
            toast.success(`Sincronização concluída! ${result.success || 0} produtos sincronizados.`);
            if (onProductsUpdated) onProductsUpdated();
          } else if (status.status === 'error') {
            toast.error(`Erro: ${status.error_message || 'Erro desconhecido'}`);
          }
        }
      }, 2000);
      
      // Timeout after 10 minutes
      setTimeout(() => {
        clearInterval(interval);
        setIsLoading(false);
      }, 600000);
      
    } catch (error) {
      toast.error('Erro ao solicitar sincronização');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkServerStatus();
    checkSyncStatus();
    
    // Check server status every 5 seconds
    const interval = setInterval(checkServerStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR');
  };

  const menuItems = [
    { id: 'sync', label: 'Sincronização', icon: RefreshCw },
    { id: 'downloads', label: 'Downloads', icon: Download },
    { id: 'config', label: 'Configurações', icon: Settings },
    { id: 'logs', label: 'Histórico', icon: FileText },
    { id: 'help', label: 'Ajuda', icon: HelpCircle },
  ];

  return (
    <div className="flex gap-6 min-h-[600px]">
      {/* Sidebar */}
      <div className="w-64 flex-shrink-0">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Server Status Header */}
          <div className={`p-4 ${serverStatus.online ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-rose-600'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full bg-white ${serverStatus.online ? 'animate-pulse' : ''}`} />
              <div className="text-white">
                <div className="font-semibold text-sm">Servidor Local</div>
                <div className="text-xs opacity-90">
                  {serverStatus.online ? 'Conectado' : 'Desconectado'}
                </div>
              </div>
            </div>
            {serverStatus.online && serverStatus.seconds_ago !== undefined && (
              <div className="text-xs text-white/70 mt-2">
                Último ping: {serverStatus.seconds_ago}s atrás
              </div>
            )}
          </div>

          {/* Menu Items */}
          <nav className="p-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveMenu(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                    activeMenu === item.id
                      ? 'bg-violet-50 text-violet-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Quick Action */}
          <div className="p-4 border-t border-slate-100">
            <Button
              onClick={handleRequestSync}
              className={`w-full flex items-center justify-center gap-2 ${
                serverStatus.online 
                  ? 'bg-violet-600 hover:bg-violet-700 text-white' 
                  : 'bg-slate-200 cursor-not-allowed text-slate-400'
              }`}
              disabled={isLoading || !serverStatus.online}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sincronizando...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Sincronizar Agora
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        {/* Sync Page */}
        {activeMenu === 'sync' && (
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-violet-600" />
                Status da Sincronização
              </h2>

              {isLoading && (
                <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                  <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
                  <div>
                    <div className="font-medium text-amber-800">Sincronização em andamento...</div>
                    <div className="text-sm text-amber-600">Aguardando resposta do servidor local</div>
                  </div>
                </div>
              )}

              {!serverStatus.online && !isLoading && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-red-800">Servidor local não detectado</div>
                    <div className="text-sm text-red-600 mt-1">
                      Execute o script <code className="bg-red-100 px-1.5 py-0.5 rounded">gelatti_sync.py</code> no seu computador
                    </div>
                  </div>
                </div>
              )}

              {serverStatus.online && !isLoading && (
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="font-medium text-green-800">Pronto para sincronizar</div>
                    <div className="text-sm text-green-600">Servidor local conectado e aguardando</div>
                  </div>
                </div>
              )}

              {/* Sync Mode Info */}
              <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg mb-4">
                <h3 className="font-medium text-violet-800 mb-2">Sincronização Inteligente</h3>
                <p className="text-sm text-violet-600">
                  O sistema verifica automaticamente cada produto:
                </p>
                <ul className="text-sm text-violet-600 mt-2 space-y-1">
                  <li>• <strong>Novos</strong> - Produtos que não existem no CRM são criados</li>
                  <li>• <strong>Atualizados</strong> - Produtos com alterações são atualizados</li>
                  <li>• <strong>Sem alteração</strong> - Produtos idênticos são ignorados</li>
                </ul>
              </div>

              {/* Stats Grid */}
              {syncStatus?.last_result && (
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <div className="text-3xl font-bold text-slate-800">
                      {syncStatus.last_result.total || 0}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">Total</div>
                  </div>
                  
                  <div className="p-4 bg-green-50 rounded-xl text-center">
                    <div className="text-3xl font-bold text-green-600">
                      {syncStatus.last_result.success || 0}
                    </div>
                    <div className="text-sm text-green-600 mt-1">Sucesso</div>
                  </div>
                  
                  <div className="p-4 bg-red-50 rounded-xl text-center">
                    <div className="text-3xl font-bold text-red-600">
                      {syncStatus.last_result.errors || 0}
                    </div>
                    <div className="text-sm text-red-600 mt-1">Erros</div>
                  </div>
                </div>
              )}

              {syncStatus?.completed_at && (
                <div className="mt-4 text-sm text-slate-500 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Última sincronização: {formatDate(syncStatus.completed_at)}
                </div>
              )}
            </div>

            {/* Progress/Activity */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-md font-semibold text-slate-700 mb-4">Atividade Recente</h3>
              
              <div className="space-y-3">
                {syncStatus?.status === 'completed' && syncStatus?.completed_at && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-700">Sincronização concluída</div>
                      <div className="text-xs text-slate-500">{formatDate(syncStatus.completed_at)}</div>
                    </div>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      {syncStatus.last_result?.success || 0} produtos
                    </span>
                  </div>
                )}
                
                {syncStatus?.status === 'error' && (
                  <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-red-700">Erro na sincronização</div>
                      <div className="text-xs text-red-500">{syncStatus.error_message}</div>
                    </div>
                  </div>
                )}

                {!syncStatus?.completed_at && !syncStatus?.error_at && (
                  <div className="text-sm text-slate-400 text-center py-4">
                    Nenhuma atividade recente
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Config Page */}
        {activeMenu === 'config' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-800 mb-6">Configurações de Conexão</h2>
            
            <div className="space-y-6">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium text-slate-700 mb-3">PostgreSQL (Gelatti)</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Host:</span>
                    <span className="ml-2 font-mono text-slate-700">192.168.53.200</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Porta:</span>
                    <span className="ml-2 font-mono text-slate-700">5432</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Banco:</span>
                    <span className="ml-2 font-mono text-slate-700">Gelatti</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Tabela:</span>
                    <span className="ml-2 font-mono text-slate-700">produtos</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium text-slate-700 mb-3">Mapeamento de Campos</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 w-32">Nome:</span>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                    <span className="font-mono text-violet-600">prodesc</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 w-32">Código:</span>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                    <span className="font-mono text-violet-600">codigo</span>
                  </div>
                </div>
              </div>

              <div className="text-sm text-slate-500">
                Para alterar as configurações, edite o arquivo <code className="bg-slate-100 px-1.5 py-0.5 rounded">servidor_gelatti.py</code>
              </div>
            </div>
          </div>
        )}

        {/* Logs Page */}
        {activeMenu === 'logs' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-800 mb-6">Histórico de Sincronizações</h2>
            
            <div className="text-sm text-slate-500 text-center py-8">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p>O histórico detalhado está disponível nos logs do servidor local</p>
              <p className="text-xs mt-2">Execute o servidor e acompanhe o terminal</p>
            </div>
          </div>
        )}

        {/* Help Page */}
        {activeMenu === 'help' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-800 mb-6">Como Usar</h2>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-medium text-slate-700">Execute o servidor local</h3>
                  <p className="text-sm text-slate-500 mt-1">No seu computador, abra o terminal e execute:</p>
                  <code className="block mt-2 p-3 bg-slate-900 text-green-400 rounded-lg text-sm font-mono">
                    py servidor_gelatti.py
                  </code>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-medium text-slate-700">Aguarde a conexão</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    O indicador no menu lateral vai mudar para <span className="text-green-600 font-medium">verde (Conectado)</span>
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-medium text-slate-700">Sincronize</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Clique em <strong>"Sincronizar Agora"</strong> para importar os produtos do PostgreSQL
                  </p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <strong>Importante:</strong> O servidor local deve estar rodando no mesmo computador que tem acesso à rede do PostgreSQL (192.168.53.x)
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Downloads Page */}
        {activeMenu === 'downloads' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
                <Download className="w-5 h-5 text-violet-600" />
                Downloads
              </h2>
              
              <p className="text-sm text-slate-600 mb-6">
                Baixe os arquivos necessários para executar a sincronização no seu computador.
              </p>

              <div className="space-y-4">
                {/* Script Python */}
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-violet-300 hover:bg-violet-50/50 transition-all">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">gelatti_sync.py</h3>
                      <p className="text-sm text-slate-500">Script Python para sincronização</p>
                    </div>
                  </div>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/gelatti/download/gelatti_sync.py`}
                    download
                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Baixar
                  </a>
                </div>

                {/* Script BAT */}
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-violet-300 hover:bg-violet-50/50 transition-all">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">iniciar_gelatti.bat</h3>
                      <p className="text-sm text-slate-500">Script Windows - Inicia facilmente</p>
                    </div>
                  </div>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/gelatti/download/iniciar_gelatti.bat`}
                    download
                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Baixar
                  </a>
                </div>
              </div>

              {/* Instructions */}
              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-700 mb-3">Instruções:</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600">
                  <li>Baixe os dois arquivos na mesma pasta</li>
                  <li>Edite o arquivo <code className="bg-slate-200 px-1 rounded">gelatti_sync.py</code> e configure a senha do PostgreSQL</li>
                  <li>Execute o arquivo <code className="bg-slate-200 px-1 rounded">iniciar_gelatti.bat</code> (ou <code className="bg-slate-200 px-1 rounded">py gelatti_sync.py</code>)</li>
                  <li>Mantenha o programa aberto enquanto usa o CRM</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminPanel = ({ onClose }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [crmContacts, setCrmContacts] = useState([]);
  const [crmStats, setCrmStats] = useState({});
  const [crmTags, setCrmTags] = useState([]);
  const [crmPipeline, setCrmPipeline] = useState([]);
  const [promoBanners, setPromoBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taskFromContact, setTaskFromContact] = useState(null);
  const [taskToOpen, setTaskToOpen] = useState(null);
  
  // Modals
  const [categoryModal, setCategoryModal] = useState({ open: false, category: null });
  const [productModal, setProductModal] = useState({ open: false, product: null });
  const [quoteDetailModal, setQuoteDetailModal] = useState({ open: false, quote: null });
  const [passwordModal, setPasswordModal] = useState(false);
  const [newUserModal, setNewUserModal] = useState(false);
  const [contactModal, setContactModal] = useState({ open: false, contact: null });
  const [contactDetailModal, setContactDetailModal] = useState({ open: false, contact: null });
  const [bannerModal, setBannerModal] = useState({ open: false, banner: null });
  const [catalogCategory, setCatalogCategory] = useState('all');
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  // Product filters
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isDeletingProducts, setIsDeletingProducts] = useState(false);
  
  // Gelatti server status
  const [gelattiServerOnline, setGelattiServerOnline] = useState(false);

  // Cevador state
  const [cevadorConfig, setCevadorConfig] = useState(null);
  const [cevadorModelModal, setCevadorModelModal] = useState({ open: false, model: null });
  const [savingCevadorConfig, setSavingCevadorConfig] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);

  // Customer management state
  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [deletingCustomerId, setDeletingCustomerId] = useState(null);

  // Available sections for permission control
  const ALL_SECTIONS = [
    { id: 'settings', name: 'Configurações', icon: Settings, color: 'slate' },
    { id: 'categories', name: 'Categorias', icon: FolderOpen, color: 'teal' },
    { id: 'products', name: 'Produtos', icon: Package, color: 'teal' },
    { id: 'quotes', name: 'Orçamentos', icon: FileText, color: 'blue' },
    { id: 'customers', name: 'Clientes', icon: Users, color: 'green' },
    { id: 'crm', name: 'CRM', icon: Users, color: 'purple' },
    { id: 'tasks', name: 'Tarefas', icon: ClipboardList, color: 'indigo' },
    { id: 'users', name: 'Usuários', icon: User, color: 'slate' },
    { id: 'promos', name: 'Promoções', icon: Sparkles, color: 'pink' },
    { id: 'cevador', name: 'Cevador', icon: Circle, color: 'emerald' },
    { id: 'gelatti', name: 'Gelatti', icon: Database, color: 'violet' },
  ];

  // Check if user has access to a section
  // Empty permissions array = admin (all access)
  const hasAccess = (sectionId) => {
    if (!currentUser) return false;
    const permissions = currentUser.permissions || [];
    // Empty permissions = full access (admin)
    if (permissions.length === 0) return true;
    return permissions.includes(sectionId);
  };

  // Check if user is admin (has full access)
  const isAdmin = () => {
    if (!currentUser) return false;
    const permissions = currentUser.permissions || [];
    return permissions.length === 0;
  };

  // Permission modal state
  const [permissionModal, setPermissionModal] = useState({ open: false, user: null, permissions: [] });

  // Handle permission update
  const handleUpdatePermissions = async () => {
    if (!permissionModal.user) return;
    try {
      await updateUserPermissions(permissionModal.user.id, permissionModal.permissions);
      toast.success('Permissões atualizadas com sucesso!');
      setPermissionModal({ open: false, user: null, permissions: [] });
      await fetchAllData();
    } catch (error) {
      toast.error('Erro ao atualizar permissões');
    }
  };

  // Toggle permission in modal
  const togglePermission = (sectionId) => {
    setPermissionModal(prev => {
      const newPermissions = prev.permissions.includes(sectionId)
        ? prev.permissions.filter(p => p !== sectionId)
        : [...prev.permissions, sectionId];
      return { ...prev, permissions: newPermissions };
    });
  };

  // Handle creating task from CRM contact
  const handleCreateTaskFromContact = (contact) => {
    setTaskFromContact(contact);
    setActiveTab('tasks');
  };

  // Handle navigation from notification click
  const handleNotificationNavigate = (tab, taskId) => {
    if (tab === 'tasks') {
      setActiveTab('tasks');
      if (taskId) {
        setTaskToOpen(taskId);
      }
    }
  };

  // Clear taskToOpen after it's been handled
  const handleTaskOpened = () => {
    setTaskToOpen(null);
  };

  // Check if already authenticated
  useEffect(() => {
    const authStatus = localStorage.getItem('gelatti_admin_auth');
    const authTime = localStorage.getItem('gelatti_admin_time');
    const savedUser = localStorage.getItem('gelatti_admin_user');
    
    // Session expires after 4 hours
    if (authStatus === 'true' && authTime) {
      const elapsed = Date.now() - parseInt(authTime);
      const fourHours = 4 * 60 * 60 * 1000;
      if (elapsed < fourHours) {
        setIsAuthenticated(true);
        if (savedUser) {
          try {
            setCurrentUser(JSON.parse(savedUser));
          } catch (e) {
            console.error('Error parsing user:', e);
          }
        }
      } else {
        localStorage.removeItem('gelatti_admin_auth');
        localStorage.removeItem('gelatti_admin_time');
        localStorage.removeItem('gelatti_admin_user');
        localStorage.removeItem('gelatti_admin_token');
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Set initial tab based on user permissions
  useEffect(() => {
    if (currentUser && !loading) {
      const permissions = currentUser.permissions || [];
      // If user is admin (empty permissions) or has access to settings, stay on settings
      if (permissions.length === 0 || permissions.includes('settings')) {
        return;
      }
      // Otherwise, find the first allowed tab
      const tabOrder = ['settings', 'categories', 'products', 'quotes', 'crm', 'tasks', 'users', 'promos'];
      const firstAllowedTab = tabOrder.find(tab => permissions.includes(tab));
      if (firstAllowedTab && firstAllowedTab !== activeTab) {
        setActiveTab(firstAllowedTab);
      }
    }
  }, [currentUser, loading]);

  // Keyboard shortcut: Ctrl+Q to open Quick Add
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'q') {
        e.preventDefault();
        window.open('/quick', '_blank', 'width=400,height=600,menubar=no,toolbar=no');
      }
    };
    
    if (isAuthenticated) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isAuthenticated]);

  const handleLogin = (authenticated, user) => {
    setIsAuthenticated(authenticated);
    setCurrentUser(user);
  };

  const handleLogout = () => {
    localStorage.removeItem('gelatti_admin_auth');
    localStorage.removeItem('gelatti_admin_time');
    localStorage.removeItem('gelatti_admin_user');
    localStorage.removeItem('gelatti_admin_token');
    setIsAuthenticated(false);
    setCurrentUser(null);
    onClose();
  };

  const reloadProducts = async () => {
    try {
      const productsData = await getProducts(null, false);
      setProducts(productsData);
    } catch (error) {
      console.error('Error reloading products:', error);
    }
  };

  const fetchAllData = async () => {
    try {
      const [settingsData, categoriesData, productsData, quotesData, usersData, contactsData, statsData, tagsData, bannersData, pipelineData, cevadorConfigData, customersData] = await Promise.all([
        getSettings(),
        getCategories(false),
        getProducts(null, false),
        getQuotesWithCustomers(),
        getAdminUsers(),
        getCrmContacts(),
        getCrmStats(),
        getCrmTags(),
        getPromoBanners(false),
        getCrmPipeline(),
        getCevadorConfig().catch(() => null),
        getAdminCustomers().catch(() => [])
      ]);
      setSettings(settingsData);
      setCategories(categoriesData);
      setProducts(productsData);
      setQuotes(quotesData);
      setAdminUsers(usersData);
      setCrmContacts(contactsData);
      setCrmStats(statsData);
      setCrmTags(tagsData);
      setPromoBanners(bannersData);
      setCrmPipeline(pipelineData);
      if (cevadorConfigData) setCevadorConfig(cevadorConfigData);
      // CevadorModels will be loaded lazily by CevadorSection when tab is active
      setCustomers(customersData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // WebSocket handler for real-time updates
  const handleWebSocketMessage = useCallback((message) => {
    const { event, type, data } = message;
    
    if (type === 'crm') {
      if (event === 'created' && data?.contact) {
        setCrmContacts(prev => [data.contact, ...prev]);
        toast.info(`Novo contato: ${data.contact.name}`, { duration: 3000 });
      } else if (event === 'updated' && data?.contact) {
        setCrmContacts(prev => prev.map(c => c.id === data.contact.id ? data.contact : c));
      } else if (event === 'deleted' && data?.contact_id) {
        setCrmContacts(prev => prev.filter(c => c.id !== data.contact_id));
      }
    } else if (type === 'quote') {
      if (event === 'created' && data?.quote) {
        setQuotes(prev => [data.quote, ...prev]);
        toast.info(`Novo orçamento: ${data.quote.customer_name}`, { duration: 3000 });
      } else if (event === 'updated' && data?.quote) {
        setQuotes(prev => prev.map(q => q.id === data.quote.id ? data.quote : q));
      } else if (event === 'deleted' && data?.quote_id) {
        setQuotes(prev => prev.filter(q => q.id !== data.quote_id));
      }
    } else if (type === 'task') {
      // Tasks will be handled by TasksSection component
      // Trigger a refresh event
      window.dispatchEvent(new CustomEvent('task-update', { detail: { event, data } }));
    }
  }, []);

  // Initialize WebSocket connection
  const { isConnected: wsConnected } = useWebSocket(handleWebSocketMessage, isAuthenticated);

  // Manual refresh functions
  const refreshCrm = async () => {
    try {
      const contactsData = await getCrmContacts();
      setCrmContacts(contactsData);
      toast.success('CRM atualizado!');
    } catch (error) {
      toast.error('Erro ao atualizar CRM');
    }
  };

  const refreshQuotes = async () => {
    try {
      const quotesData = await getQuotesWithCustomers();
      setQuotes(quotesData);
      toast.success('Orçamentos atualizados!');
    } catch (error) {
      toast.error('Erro ao atualizar orçamentos');
    }
  };

  // Check Gelatti server status periodically
  useEffect(() => {
    const checkGelattiStatus = async () => {
      try {
        const status = await getGelattiServerStatus();
        setGelattiServerOnline(status?.online || false);
      } catch {
        setGelattiServerOnline(false);
      }
    };
    
    checkGelattiStatus();
    const interval = setInterval(checkGelattiStatus, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);

  // Settings handlers
  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      alert('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const result = await uploadLogo(file);
      setSettings(prev => ({ ...prev, logo_url: result.logo_url }));
      alert('Logo atualizada com sucesso!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Erro ao fazer upload da logo');
    }
  };

  // Category handlers
  const handleSaveCategory = async (categoryData) => {
    try {
      if (categoryData.id) {
        await updateCategory(categoryData.id, categoryData);
      } else {
        await createCategory(categoryData);
      }
      await fetchAllData();
      setCategoryModal({ open: false, category: null });
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Erro ao salvar categoria');
    }
  };

  // Customer handlers
  const handleDeleteCustomer = async (customerId, customerName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o cliente "${customerName}"? Esta ação não pode ser desfeita.`)) return;
    try {
      setDeletingCustomerId(customerId);
      await deleteAdminCustomer(customerId);
      setCustomers(prev => prev.filter(c => c.id !== customerId));
      toast.success('Cliente excluído com sucesso!');
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Erro ao excluir cliente');
    } finally {
      setDeletingCustomerId(null);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Tem certeza que deseja remover esta categoria?')) return;
    try {
      await deleteCategory(categoryId);
      await fetchAllData();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Erro ao remover categoria');
    }
  };

  const handleCategoryImageUpload = async (categoryId, file) => {
    try {
      await uploadCategoryImage(categoryId, file);
      await fetchAllData();
    } catch (error) {
      console.error('Error uploading category image:', error);
      alert('Erro ao fazer upload da imagem');
    }
  };

  // Product handlers
  const handleSaveProduct = async (productData, pendingImage = null) => {
    try {
      let productId = productData.id;
      
      if (productData.id) {
        await updateProduct(productData.id, productData);
      } else {
        const newProduct = await createProduct(productData);
        productId = newProduct?.id;
      }
      
      // If there's a pending image for a new product, upload it now
      if (pendingImage && productId) {
        try {
          await uploadProductImage(productId, pendingImage);
        } catch (imageError) {
          console.error('Error uploading main image:', imageError);
          // Product was created, but image upload failed - inform user
          alert('Produto criado, mas houve erro ao enviar a imagem. Você pode adicionar a imagem editando o produto.');
        }
      }
      
      await fetchAllData();
      setProductModal({ open: false, product: null });
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Erro ao salvar produto');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Tem certeza que deseja remover este produto?')) return;
    try {
      await deleteProduct(productId);
      await fetchAllData();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Erro ao remover produto');
    }
  };

  const handleDeleteSelectedProducts = async () => {
    if (selectedProducts.length === 0) return;
    if (!window.confirm(`Tem certeza que deseja remover ${selectedProducts.length} produto(s)?`)) return;
    
    setIsDeletingProducts(true);
    try {
      for (const productId of selectedProducts) {
        await deleteProduct(productId);
      }
      setSelectedProducts([]);
      await fetchAllData();
    } catch (error) {
      console.error('Error deleting products:', error);
      alert('Erro ao remover produtos');
    } finally {
      setIsDeletingProducts(false);
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleAllProducts = (filteredProducts) => {
    const allFilteredIds = filteredProducts.map(p => p.id);
    const allSelected = allFilteredIds.every(id => selectedProducts.includes(id));
    
    if (allSelected) {
      setSelectedProducts(prev => prev.filter(id => !allFilteredIds.includes(id)));
    } else {
      setSelectedProducts(prev => [...new Set([...prev, ...allFilteredIds])]);
    }
  };

  const handleProductImageUpload = async (productId, file) => {
    try {
      await uploadProductImage(productId, file);
      await fetchAllData();
    } catch (error) {
      console.error('Error uploading product image:', error);
      alert('Erro ao fazer upload da imagem');
    }
  };

  const handleProductAdditionalImageUpload = async (productId, file) => {
    try {
      const result = await uploadProductAdditionalImage(productId, file);
      await fetchAllData();
      return result; // Return the result to update local state
    } catch (error) {
      console.error('Error uploading additional image:', error);
      alert('Erro ao fazer upload da imagem adicional');
      throw error;
    }
  };

  const handleDeleteProductAdditionalImage = async (productId, imageIndex) => {
    if (!window.confirm('Tem certeza que deseja remover esta imagem?')) return;
    try {
      await deleteProductAdditionalImage(productId, imageIndex);
      await fetchAllData();
    } catch (error) {
      console.error('Error deleting additional image:', error);
      alert('Erro ao remover imagem');
    }
  };

  const handleSaveProductImageAdjustment = async (productId, adjustment) => {
    try {
      await updateProductImageAdjustment(productId, adjustment);
      await fetchAllData();
    } catch (error) {
      console.error('Error saving image adjustment:', error);
      throw error;
    }
  };

  const handleGenerateCatalogPDF = async () => {
    try {
      setGeneratingPDF(true);
      const API = process.env.REACT_APP_BACKEND_URL;
      const url = `${API}/api/catalog/pdf${catalogCategory !== 'all' ? `?category_id=${catalogCategory}` : ''}`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Erro ao gerar PDF');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'catalogo.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error generating catalog PDF:', error);
      alert('Erro ao gerar catálogo PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleSaveProductAdditionalImageAdjustment = async (productId, imageIndex, adjustment) => {
    try {
      await updateProductAdditionalImageAdjustment(productId, imageIndex, adjustment);
      await fetchAllData();
    } catch (error) {
      console.error('Error saving additional image adjustment:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Carregando painel...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="fixed inset-0 bg-slate-100 z-50 overflow-auto">
      {/* Header - Mobile Responsive */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <button onClick={onClose} className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg flex-shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base sm:text-xl font-bold text-slate-800 truncate">Painel Admin</h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-4">
            <img 
              src={settings?.logo_url} 
              alt="Logo" 
              className="h-8 sm:h-10 w-auto object-contain hidden xs:block"
            />
            {currentUser && (
              <>
                <NotificationsPanel 
                  userId={currentUser.id} 
                  onNavigate={handleNotificationNavigate}
                />
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg">
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-700">{currentUser.name || currentUser.username}</span>
                </div>
              </>
            )}
            <button
              onClick={() => setPasswordModal(true)}
              className="p-1.5 sm:p-2 text-slate-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
              title="Alterar Senha"
            >
              <Key className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 sm:gap-2 p-1.5 sm:px-3 sm:py-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Sair"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline text-sm">Sair</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto">
        <div className="flex min-h-[calc(100vh-80px)]">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0 fixed left-0 top-[80px] bottom-0 flex flex-col z-40" style={{ backgroundColor: '#195C75' }}>
            {/* Menu Items - Scrollable */}
            <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
              {hasAccess('settings') && (
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    activeTab === 'settings'
                      ? 'bg-white text-[#195C75] shadow-md font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                >
                  <Settings className="w-5 h-5" />
                  <span className="font-medium">Configurações</span>
                </button>
              )}

              {hasAccess('categories') && (
                <button
                  onClick={() => setActiveTab('categories')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    activeTab === 'categories'
                      ? 'bg-white text-[#195C75] shadow-md font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                >
                  <FolderOpen className="w-5 h-5" />
                  <span className="font-medium flex-1">Categorias</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === 'categories' ? 'bg-[#195C75] text-white' : 'bg-white/20 text-white'
                  }`}>{categories.length}</span>
                </button>
              )}
              
              {hasAccess('products') && (
                <button
                  onClick={() => setActiveTab('products')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    activeTab === 'products'
                      ? 'bg-white text-[#195C75] shadow-md font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                >
                  <Package className="w-5 h-5" />
                  <span className="font-medium flex-1">Produtos</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === 'products' ? 'bg-[#195C75] text-white' : 'bg-white/20 text-white'
                  }`}>{products.length}</span>
                </button>
              )}

              {/* Section: Vendas */}
              {hasAccess('quotes') && (
                <button
                  onClick={() => setActiveTab('quotes')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    activeTab === 'quotes'
                      ? 'bg-white text-[#195C75] shadow-md font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  <span className="font-medium flex-1">Orçamentos</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === 'quotes' ? 'bg-[#195C75] text-white' : 'bg-white/20 text-white'
                  }`}>{quotes.length}</span>
                </button>
              )}
              
              {hasAccess('crm') && (
                <button
                  onClick={() => setActiveTab('crm')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    activeTab === 'crm'
                      ? 'bg-white text-[#195C75] shadow-md font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                >
                  <Users className="w-5 h-5" />
                  <span className="font-medium flex-1">CRM</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === 'crm' ? 'bg-[#195C75] text-white' : 'bg-white/20 text-white'
                  }`}>{crmContacts.length}</span>
                </button>
              )}

              {hasAccess('customers') && (
                <button
                  onClick={() => setActiveTab('customers')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    activeTab === 'customers'
                      ? 'bg-white text-[#195C75] shadow-md font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                  data-testid="sidebar-customers"
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium flex-1">Clientes</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === 'customers' ? 'bg-[#195C75] text-white' : 'bg-white/20 text-white'
                  }`}>{customers.length}</span>
                </button>
              )}
              
              {hasAccess('promos') && (
                <button
                  onClick={() => setActiveTab('promos')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    activeTab === 'promos'
                      ? 'bg-white text-[#195C75] shadow-md font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                >
                  <Sparkles className="w-5 h-5" />
                  <span className="font-medium flex-1">Promoções</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === 'promos' ? 'bg-[#195C75] text-white' : 'bg-white/20 text-white'
                  }`}>{promoBanners.length}</span>
                </button>
              )}

              {/* Section: Gestão */}
              {hasAccess('tasks') && (
                <button
                  onClick={() => setActiveTab('tasks')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    activeTab === 'tasks'
                      ? 'bg-white text-[#195C75] shadow-md font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                >
                  <ClipboardList className="w-5 h-5" />
                  <span className="font-medium">Tarefas</span>
                </button>
              )}
              
              {hasAccess('users') && (
                <button
                  onClick={() => setActiveTab('users')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    activeTab === 'users'
                      ? 'bg-white text-[#195C75] shadow-md font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium flex-1">Usuários</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === 'users' ? 'bg-[#195C75] text-white' : 'bg-white/20 text-white'
                  }`}>{adminUsers.length}</span>
                </button>
              )}

              {hasAccess('cevador') && (
                <button
                  onClick={() => setActiveTab('cevador')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    activeTab === 'cevador'
                      ? 'bg-white text-[#195C75] shadow-md font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                  data-testid="sidebar-cevador"
                >
                  <Circle className="w-5 h-5" />
                  <span className="font-medium flex-1">Cevador</span>
                </button>
              )}

              {hasAccess('settings') && (
                <button
                  onClick={() => setActiveTab('landing')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    activeTab === 'landing'
                      ? 'bg-white text-[#195C75] shadow-md font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                  data-testid="sidebar-landing"
                >
                  <ExternalLink className="w-5 h-5" />
                  <span className="font-medium">Landing Page</span>
                </button>
              )}

              {hasAccess('settings') && (
                <button
                  onClick={() => setActiveTab('integration')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    activeTab === 'integration'
                      ? 'bg-white text-[#195C75] shadow-md font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                  data-testid="sidebar-integration"
                >
                  <Database className="w-5 h-5" />
                  <span className="font-medium">Integração</span>
                </button>
              )}
            </nav>

            {/* Footer - Now at bottom of sidebar with flex */}
            <div className="mt-auto p-4 border-t border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-sm">
                  {currentUser?.username?.charAt(0)?.toUpperCase() || 'A'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white text-sm truncate">{currentUser?.username}</div>
                  <div className="text-xs text-white/60">Administrador</div>
                </div>
              </div>
              <button 
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sair</span>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 ml-64 p-6 bg-slate-50 min-h-[calc(100vh-80px)]">
            {/* Settings */}
            {activeTab === 'settings' && (
              <>
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
              <h2 className="text-base sm:text-lg font-semibold text-slate-800 mb-4 sm:mb-6">Configurações da Empresa</h2>
              
              {/* Logo Upload */}
              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">Logo</label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                  <img 
                    src={settings?.logo_url} 
                    alt="Logo atual" 
                    className="h-12 sm:h-16 w-auto object-contain bg-slate-100 rounded-lg p-2"
                  />
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-teal-50 text-teal-600 rounded-lg hover:bg-teal-100 transition-colors text-sm">
                      <Upload className="w-4 h-4" />
                      <span>Trocar Logo</span>
                    </div>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nome da Empresa</label>
                  <Input 
                    value={settings?.company_name || ''} 
                    onChange={(e) => handleSettingsChange('company_name', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Slogan</label>
                  <Input 
                    value={settings?.tagline || ''} 
                    onChange={(e) => handleSettingsChange('tagline', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">WhatsApp (com código país)</label>
                  <Input 
                    value={settings?.whatsapp || ''} 
                    onChange={(e) => handleSettingsChange('whatsapp', e.target.value)}
                    placeholder="5555984551621"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Instagram</label>
                  <Input 
                    value={settings?.instagram || ''} 
                    onChange={(e) => handleSettingsChange('instagram', e.target.value)}
                    placeholder="@gelattisolucoes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">URL do Instagram</label>
                  <Input 
                    value={settings?.instagram_url || ''} 
                    onChange={(e) => handleSettingsChange('instagram_url', e.target.value)}
                    placeholder="https://www.instagram.com/gelattisolucoes/"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">E-mail</label>
                  <Input 
                    value={settings?.email || ''} 
                    onChange={(e) => handleSettingsChange('email', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Endereço</label>
                  <Input 
                    value={settings?.address || ''} 
                    onChange={(e) => handleSettingsChange('address', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">CEP</label>
                  <Input 
                    value={settings?.cep || ''} 
                    onChange={(e) => handleSettingsChange('cep', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">CNPJ</label>
                  <Input 
                    value={settings?.cnpj || ''} 
                    onChange={(e) => handleSettingsChange('cnpj', e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">Descrição</label>
                <Textarea 
                  value={settings?.description || ''} 
                  onChange={(e) => handleSettingsChange('description', e.target.value)}
                  rows={3}
                />
              </div>

              {/* SMTP Configuration */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h4 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-teal-600" />
                  Configurações de Email (SMTP)
                </h4>
                <p className="text-sm text-slate-500 mb-4">
                  Configure o servidor SMTP para enviar emails de verificação de conta e notificações.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Servidor SMTP</label>
                    <Input 
                      value={settings?.smtp_host || ''} 
                      onChange={(e) => handleSettingsChange('smtp_host', e.target.value)}
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Porta SMTP</label>
                    <Input 
                      type="number"
                      value={settings?.smtp_port || 587} 
                      onChange={(e) => handleSettingsChange('smtp_port', parseInt(e.target.value) || 587)}
                      placeholder="587"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email SMTP</label>
                    <Input 
                      type="email"
                      value={settings?.smtp_email || ''} 
                      onChange={(e) => handleSettingsChange('smtp_email', e.target.value)}
                      placeholder="seu-email@gmail.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Senha SMTP</label>
                    <Input 
                      type="password"
                      value={settings?.smtp_password || ''} 
                      onChange={(e) => handleSettingsChange('smtp_password', e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Nome do Remetente</label>
                    <Input 
                      value={settings?.smtp_name || ''} 
                      onChange={(e) => handleSettingsChange('smtp_name', e.target.value)}
                      placeholder="Gelatti Soluções"
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <input
                      type="checkbox"
                      id="smtp_use_ssl"
                      checked={settings?.smtp_use_ssl !== false}
                      onChange={(e) => handleSettingsChange('smtp_use_ssl', e.target.checked)}
                      className="w-4 h-4 text-teal-600 rounded"
                    />
                    <label htmlFor="smtp_use_ssl" className="text-sm text-slate-700">
                      Usar SSL/TLS (recomendado)
                    </label>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Dica Gmail:</strong> Use uma "Senha de App" ao invés da senha normal. 
                    Vá em Conta Google → Segurança → Senhas de app.
                  </p>
                </div>
                
                {/* Test SMTP Button */}
                <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Testar Configuração</p>
                      <p className="text-xs text-slate-500">Envie um email de teste para verificar se a configuração está correta</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        const testEmail = prompt('Digite o email para receber o teste:', settings?.smtp_email || settings?.email || '');
                        if (testEmail) {
                          try {
                            setSmtpTesting(true);
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/test-smtp`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ to_email: testEmail })
                            });
                            const result = await response.json();
                            if (result.success) {
                              alert('✅ ' + result.message);
                            } else {
                              alert('❌ ' + result.message);
                            }
                          } catch (err) {
                            alert('❌ Erro ao testar: ' + err.message);
                          } finally {
                            setSmtpTesting(false);
                          }
                        }
                      }}
                      disabled={smtpTesting || !settings?.smtp_host || !settings?.smtp_email || !settings?.smtp_password}
                      className="flex items-center gap-2"
                    >
                      {smtpTesting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Testando...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Enviar Teste
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button 
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Salvando...' : 'Salvar Configurações'}
                </Button>
              </div>
            </div>

            {/* Database Backup Section */}
            <DatabaseBackupSection />
            </>
            )}

          {/* Categories */}
          {activeTab === 'categories' && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-800">Categorias</h2>
                <Button 
                  onClick={() => setCategoryModal({ open: true, category: null })}
                  className="bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Nova Categoria
                </Button>
              </div>

              {/* Simple List View */}
              <div className="bg-slate-50 rounded-xl overflow-hidden">
                <div className="divide-y divide-slate-200">
                  {categories.map((cat, index) => (
                    <div 
                      key={cat.id} 
                      className={`flex items-center justify-between p-4 hover:bg-white transition-colors ${!cat.active ? 'bg-red-50' : ''}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold ${
                          ['bg-teal-500', 'bg-blue-500', 'bg-purple-500', 'bg-amber-500', 'bg-pink-500', 'bg-indigo-500', 'bg-green-500', 'bg-red-500'][index % 8]
                        }`}>
                          {cat.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-800 flex items-center gap-2">
                            {cat.name}
                            {cat.show_in_menu && (
                              <span className="text-xs bg-[#195C75]/10 text-[#195C75] px-2 py-0.5 rounded flex items-center gap-1">
                                <Menu className="w-3 h-3" />
                                Menu
                              </span>
                            )}
                            {!cat.active && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Inativo</span>
                            )}
                          </h3>
                          <p className="text-sm text-slate-500">{cat.description || 'Sem descrição'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-400 mr-2">
                          {products.filter(p => p.category_id === cat.id || p.category_ids?.includes(cat.id)).length} produtos
                        </span>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => {
                            const baseUrl = window.location.origin;
                            const categoryUrl = `${baseUrl}/?categoria=${cat.id}`;
                            navigator.clipboard.writeText(categoryUrl);
                            toast.success('Link copiado!', { description: categoryUrl });
                          }}
                          title="Copiar link da categoria"
                          className="text-[#195C75] hover:text-[#195C75] hover:bg-[#195C75]/10"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => {
                            const baseUrl = window.location.origin;
                            const categoryUrl = `${baseUrl}/?categoria=${cat.id}`;
                            window.open(categoryUrl, '_blank');
                          }}
                          title="Abrir categoria em nova aba"
                          className="text-slate-500 hover:text-slate-700"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setCategoryModal({ open: true, category: cat })}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleDeleteCategory(cat.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {categories.length === 0 && (
                    <div className="p-8 text-center text-slate-500">
                      <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nenhuma categoria cadastrada</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Products */}
          {activeTab === 'products' && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex flex-col gap-4 mb-6">
                {/* Header Row */}
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-800">Produtos</h2>
                  <Button 
                    onClick={() => setProductModal({ open: true, product: null })}
                    className="bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2"
                    data-testid="new-product-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Novo Produto
                  </Button>
                </div>
                
                {/* Filters Row */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Search Input */}
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Buscar produto por nome, código ou descrição..."
                      value={productSearchTerm}
                      onChange={(e) => setProductSearchTerm(e.target.value)}
                      className="pl-10 h-10"
                      data-testid="product-search-input"
                    />
                  </div>
                  
                  {/* Category Filter */}
                  <select
                    value={productCategoryFilter}
                    onChange={(e) => setProductCategoryFilter(e.target.value)}
                    className="h-10 px-3 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[180px]"
                    data-testid="product-category-filter"
                  >
                    <option value="all">Todas as Categorias</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Catalog PDF Generation */}
                <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                  <span className="text-sm text-slate-500">Gerar catálogo:</span>
                  <select
                    value={catalogCategory}
                    onChange={(e) => setCatalogCategory(e.target.value)}
                    className="h-9 px-3 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="all">Todas as Categorias</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <Button 
                    onClick={handleGenerateCatalogPDF}
                    variant="outline"
                    className="flex items-center gap-2"
                    disabled={generatingPDF}
                  >
                    <FileText className="w-4 h-4" />
                    {generatingPDF ? 'Gerando...' : 'Gerar PDF'}
                  </Button>
                </div>
              </div>

              {/* Filtered Products Count */}
              {(() => {
                const filteredProducts = products.filter(prod => {
                  // Category filter
                  const matchesCategory = productCategoryFilter === 'all' || 
                    prod.category_id === productCategoryFilter || 
                    prod.category_ids?.includes(productCategoryFilter);
                  
                  // Search filter
                  const searchLower = productSearchTerm.toLowerCase().trim();
                  const matchesSearch = !searchLower || 
                    prod.name?.toLowerCase().includes(searchLower) ||
                    prod.code?.toLowerCase().includes(searchLower) ||
                    prod.description?.toLowerCase().includes(searchLower);
                  
                  return matchesCategory && matchesSearch;
                });
                
                return (
                  <>
                    {(productSearchTerm || productCategoryFilter !== 'all') && (
                      <div className="flex items-center justify-between mb-4 p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm text-slate-600">
                          {filteredProducts.length} {filteredProducts.length === 1 ? 'produto encontrado' : 'produtos encontrados'}
                        </span>
                        {(productSearchTerm || productCategoryFilter !== 'all') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setProductSearchTerm('');
                              setProductCategoryFilter('all');
                            }}
                            className="text-slate-500 hover:text-slate-700"
                            data-testid="clear-product-filters-btn"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Limpar filtros
                          </Button>
                        )}
                      </div>
                    )}
                    
                    {/* Products Table/List */}
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      {/* Selection Actions Bar */}
                      {selectedProducts.length > 0 && (
                        <div className="bg-[#195C75] px-4 py-3 flex items-center justify-between">
                          <span className="text-white text-sm font-medium">
                            {selectedProducts.length} produto(s) selecionado(s)
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setSelectedProducts([])}
                              className="text-white hover:bg-white/20"
                            >
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleDeleteSelectedProducts}
                              disabled={isDeletingProducts}
                              className="bg-red-500 hover:bg-red-600 text-white"
                              data-testid="delete-selected-products-btn"
                            >
                              {isDeletingProducts ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  Excluindo...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Excluir Selecionados
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Header */}
                      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 grid grid-cols-12 gap-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <div className="col-span-1 flex items-center">
                          <input
                            type="checkbox"
                            checked={filteredProducts.length > 0 && filteredProducts.every(p => selectedProducts.includes(p.id))}
                            onChange={() => toggleAllProducts(filteredProducts)}
                            className="w-4 h-4 rounded border-slate-300 text-[#195C75] focus:ring-[#195C75]"
                            data-testid="select-all-products"
                          />
                        </div>
                        <div className="col-span-1">Img</div>
                        <div className="col-span-2">Código</div>
                        <div className="col-span-3">Nome</div>
                        <div className="col-span-2">Categoria</div>
                        <div className="col-span-1 text-right">Mín.</div>
                        <div className="col-span-1 text-right">Preço</div>
                        <div className="col-span-1 text-right">Ações</div>
                      </div>
                      
                      {/* Rows */}
                      <div className="divide-y divide-slate-100">
                        {filteredProducts.map(prod => {
                          const category = categories.find(c => c.id === prod.category_id || prod.category_ids?.includes(c.id));
                          const lowestPrice = prod.prices && prod.prices.length > 0 
                            ? prod.prices[prod.prices.length - 1]?.price 
                            : null;
                          const isSelected = selectedProducts.includes(prod.id);
                          
                          return (
                            <div 
                              key={prod.id} 
                              className={`px-4 py-3 grid grid-cols-12 gap-4 items-center hover:bg-slate-50 transition-colors ${
                                !prod.active ? 'bg-red-50 opacity-60' : ''
                              } ${isSelected ? 'bg-[#195C75]/5' : ''}`}
                            >
                              {/* Checkbox */}
                              <div className="col-span-1 flex items-center">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleProductSelection(prod.id)}
                                  className="w-4 h-4 rounded border-slate-300 text-[#195C75] focus:ring-[#195C75]"
                                  data-testid={`select-product-${prod.id}`}
                                />
                              </div>
                              
                              {/* Thumbnail */}
                              <div className="col-span-1">
                                <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                                  <img 
                                    src={prod.image_url || 'https://via.placeholder.com/80x80?text=?'} 
                                    alt={prod.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              </div>
                              
                              {/* Code */}
                              <div className="col-span-2">
                                <span className="font-mono text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                                  {prod.code || '-'}
                                </span>
                              </div>
                              
                              {/* Name */}
                              <div className="col-span-3">
                                <div className="flex items-center gap-1">
                                  {prod.featured && (
                                    <Star className="w-4 h-4 text-[#195C75] fill-[#195C75] flex-shrink-0" />
                                  )}
                                  <p className="font-medium text-slate-800 truncate">{prod.name}</p>
                                </div>
                                {prod.description && (
                                  <p className="text-xs text-slate-400 truncate">{prod.description}</p>
                                )}
                              </div>
                              
                              {/* Category */}
                              <div className="col-span-2">
                                {category ? (
                                  <span className="text-xs bg-[#195C75]/10 text-[#195C75] px-2 py-1 rounded-full">
                                    {category.name}
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400">-</span>
                                )}
                              </div>
                              
                              {/* Min Qty */}
                              <div className="col-span-1 text-right">
                                <span className="text-sm text-slate-600">{prod.min_quantity || 1}</span>
                              </div>
                              
                              {/* Price */}
                              <div className="col-span-1 text-right">
                                {lowestPrice ? (
                                  <span className="text-sm font-medium text-teal-600">
                                    R$ {lowestPrice.toFixed(2)}
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400">-</span>
                                )}
                              </div>
                              
                              {/* Actions */}
                              <div className="col-span-1 flex justify-end gap-1">
                                <button 
                                  onClick={() => {
                                    // Create a copy of the product without id
                                    const productCopy = {
                                      ...prod,
                                      id: undefined,
                                      name: `${prod.name} (Cópia)`,
                                      code: prod.code ? `${prod.code}-COPIA` : '',
                                      image_url: prod.image_url, // Keep the same image
                                      images: prod.images || [],
                                      category_ids: prod.category_ids || (prod.category_id ? [prod.category_id] : []),
                                    };
                                    setProductModal({ open: true, product: productCopy, isDuplicate: true });
                                  }}
                                  className="p-1.5 text-slate-400 hover:text-[#195C75] hover:bg-[#195C75]/10 rounded transition-colors"
                                  title="Duplicar produto"
                                  data-testid={`duplicate-product-${prod.id}`}
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => setProductModal({ open: true, product: prod })}
                                  className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                                  title="Editar"
                                  data-testid={`edit-product-${prod.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteProduct(prod.id)}
                                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Excluir"
                                  data-testid={`delete-product-${prod.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
              
              {/* No products message */}
              {filteredProducts.length === 0 && (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">
                    {productSearchTerm || productCategoryFilter !== 'all' 
                      ? 'Nenhum produto encontrado com os filtros aplicados.' 
                      : 'Nenhum produto cadastrado.'}
                  </p>
                </div>
              )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Quotes */}
          {activeTab === 'quotes' && (
            <QuotesSection 
              quotes={quotes}
              contacts={crmContacts}
              onRefresh={fetchAllData}
              onManualRefresh={refreshQuotes}
              wsConnected={wsConnected}
            />
          )}

          {/* Customers */}
          {activeTab === 'customers' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800">Clientes Cadastrados</h2>
                    <p className="text-sm text-slate-500">{customers.length} clientes no total</p>
                  </div>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar cliente..."
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Customer List */}
                <div className="space-y-3">
                  {customers
                    .filter(c => {
                      if (!customerSearchTerm) return true;
                      const search = customerSearchTerm.toLowerCase();
                      return (
                        c.name?.toLowerCase().includes(search) ||
                        c.email?.toLowerCase().includes(search) ||
                        c.phone?.includes(search) ||
                        c.cpf_cnpj?.includes(search)
                      );
                    })
                    .map(customer => (
                      <div key={customer.id} className="p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4 flex-1 min-w-0">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-green-700 font-bold text-lg">
                                {customer.name?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold text-slate-800 truncate">{customer.name}</p>
                                {customer.email_verified ? (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                                    <Check className="w-3 h-3" />
                                    Verificado
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                                    Pendente
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
                                {customer.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {customer.phone}
                                  </span>
                                )}
                                {customer.email && (
                                  <span className="flex items-center gap-1 truncate">
                                    <Mail className="w-3 h-3" />
                                    {customer.email}
                                  </span>
                                )}
                              </div>
                              {customer.cpf_cnpj && (
                                <p className="text-xs text-slate-400 mt-1">
                                  {customer.cpf_cnpj.length === 11 ? 'CPF' : 'CNPJ'}: {
                                    customer.cpf_cnpj.length === 11 
                                      ? customer.cpf_cnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
                                      : customer.cpf_cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
                                  }
                                </p>
                              )}
                              {customer.city && customer.state && (
                                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {customer.city}/{customer.state}
                                </p>
                              )}
                              <p className="text-xs text-slate-400 mt-2">
                                Cadastrado em: {new Date(customer.created_at).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteCustomer(customer.id, customer.name)}
                              disabled={deletingCustomerId === customer.id}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              {deletingCustomerId === customer.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  }
                  {customers.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Nenhum cliente cadastrado ainda</p>
                    </div>
                  )}
                  {customers.length > 0 && customers.filter(c => {
                    if (!customerSearchTerm) return true;
                    const search = customerSearchTerm.toLowerCase();
                    return (
                      c.name?.toLowerCase().includes(search) ||
                      c.email?.toLowerCase().includes(search) ||
                      c.phone?.includes(search) ||
                      c.cpf_cnpj?.includes(search)
                    );
                  }).length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <Search className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Nenhum cliente encontrado para "{customerSearchTerm}"</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Users */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              {/* 2FA Settings for current user */}
              {currentUser && (
                <TwoFactorSettings userId={currentUser.id} userName={currentUser.name} />
              )}

              {/* Users List */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-slate-800">Usuários Administradores</h2>
                  {isAdmin() && (
                    <Button
                      onClick={() => setNewUserModal(true)}
                      className="bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      Novo Usuário
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  {adminUsers.map(user => (
                    <div key={user.id} className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">{user.name}</p>
                            <p className="text-sm text-slate-500">@{user.username}</p>
                            {user.last_login && (
                              <p className="text-xs text-slate-400">
                                Último acesso: {new Date(user.last_login).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {user.totp_enabled && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              2FA
                            </span>
                          )}
                          {(!user.permissions || user.permissions.length === 0) && (
                            <span className="px-2 py-1 bg-[#195C75]/10 text-[#195C75] text-xs font-medium rounded-full flex items-center gap-1">
                              <Key className="w-3 h-3" />
                              Admin
                            </span>
                          )}
                          {currentUser?.id === user.id && (
                            <span className="px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">
                              Você
                            </span>
                          )}
                          {/* Permission Button - Only admin can edit, and not their own */}
                          {isAdmin() && currentUser?.id !== user.id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPermissionModal({ 
                                open: true, 
                                user: user, 
                                permissions: user.permissions || [] 
                              })}
                              className="text-blue-600 hover:text-blue-700"
                              title="Gerenciar permissões"
                            >
                              <Lock className="w-4 h-4" />
                            </Button>
                          )}
                          {adminUsers.length > 1 && currentUser?.id !== user.id && isAdmin() && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                if (window.confirm(`Tem certeza que deseja remover o usuário ${user.name}?`)) {
                                  try {
                                    await deleteAdminUser(user.id);
                                    await fetchAllData();
                                    toast.success('Usuário removido com sucesso');
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Erro ao remover usuário');
                                  }
                                }
                              }}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Show current permissions */}
                      {user.permissions && user.permissions.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs text-slate-500 mb-2">Acesso permitido:</p>
                          <div className="flex flex-wrap gap-1">
                            {user.permissions.map(perm => {
                              const section = ALL_SECTIONS.find(s => s.id === perm);
                              return section ? (
                                <span 
                                  key={perm}
                                  className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded-full"
                                >
                                  {section.name}
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Integration */}
          {activeTab === 'integration' && (
            <IntegrationSection onProductsUpdated={fetchAllData} />
          )}

          {/* Landing Page */}
          {activeTab === 'landing' && (
            <LandingSection />
          )}

          {/* CRM */}
          {activeTab === 'crm' && (
            <CrmSection 
              contacts={crmContacts}
              stats={crmStats}
              tags={crmTags}
              pipeline={crmPipeline}
              currentUser={currentUser}
              onRefresh={fetchAllData}
              onManualRefresh={refreshCrm}
              wsConnected={wsConnected}
              onOptimisticStatusChange={(contactId, newStatusId) => {
                // Immediately update local state for instant feedback
                setCrmContacts(prev => prev.map(c => 
                  c.id === contactId 
                    ? { ...c, status_id: newStatusId, status: newStatusId }
                    : c
                ));
              }}
              onOpenContact={(contact) => setContactDetailModal({ open: true, contact })}
              onEditContact={(contact) => setContactModal({ open: true, contact })}
              onNewContact={() => setContactModal({ open: true, contact: null })}
              onPipelineUpdate={async () => {
                const newPipeline = await getCrmPipeline();
                setCrmPipeline(newPipeline);
              }}
              onCreateTaskFromContact={handleCreateTaskFromContact}
            />
          )}

          {/* Promos */}
          {activeTab === 'promos' && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-800">Banners Promocionais</h2>
                <Button 
                  onClick={() => setBannerModal({ open: true, banner: null })}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Banner
                </Button>
              </div>

              <p className="text-sm text-slate-500 mb-6">
                Crie banners promocionais para destacar produtos, lançamentos ou promoções na página inicial.
              </p>

              {promoBanners.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nenhum banner promocional</p>
                  <p className="text-sm text-slate-400">Crie seu primeiro banner para destacar produtos</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {promoBanners.map(banner => (
                    <div key={banner.id} className={`border rounded-xl overflow-hidden ${banner.active ? 'border-slate-200' : 'border-red-200 bg-red-50'}`}>
                      {/* Banner Image */}
                      <div className="relative aspect-video bg-gradient-to-br from-[#0F5156] to-[#1B93A4]">
                        {banner.image_url ? (
                          <img 
                            src={banner.image_url} 
                            alt={banner.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white/50">
                            <ImageIcon className="w-12 h-12" />
                          </div>
                        )}
                        {!banner.active && (
                          <span className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                            Inativo
                          </span>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-slate-800 mb-1 truncate">{banner.title}</h3>
                        {banner.subtitle && (
                          <p className="text-sm text-slate-500 truncate mb-2">{banner.subtitle}</p>
                        )}
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setBannerModal({ open: true, banner })}
                            className="flex-1"
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={async () => {
                              if (window.confirm('Remover este banner?')) {
                                await deletePromoBanner(banner.id);
                                fetchAllData();
                              }
                            }}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tasks */}
          {activeTab === 'tasks' && (
            <TasksSection 
              users={adminUsers} 
              contactForNewTask={taskFromContact}
              onTaskCreatedFromContact={() => setTaskFromContact(null)}
              currentUser={currentUser}
              taskToOpen={taskToOpen}
              onTaskOpened={handleTaskOpened}
              wsConnected={wsConnected}
            />
          )}

          {/* Cevador */}
          {activeTab === 'cevador' && (
            <CevadorSection 
              config={cevadorConfig}
              onConfigUpdate={async (newConfig) => {
                setSavingCevadorConfig(true);
                try {
                  await updateCevadorConfig(newConfig);
                  setCevadorConfig(prev => ({ ...prev, ...newConfig }));
                  toast.success('Configurações salvas!');
                } catch (error) {
                  toast.error('Erro ao salvar configurações');
                } finally {
                  setSavingCevadorConfig(false);
                }
              }}
              onModelCreate={async (model) => {
                try {
                  const newModel = await createCevadorModel(model);
                  toast.success('Modelo criado!');
                  return newModel;
                } catch (error) {
                  toast.error('Erro ao criar modelo');
                  throw error;
                }
              }}
              onModelUpdate={async (modelId, data) => {
                try {
                  await updateCevadorModel(modelId, data);
                  toast.success('Modelo atualizado!');
                } catch (error) {
                  toast.error('Erro ao atualizar modelo');
                }
              }}
              onModelDelete={async (modelId) => {
                try {
                  await deleteCevadorModel(modelId);
                  toast.success('Modelo removido!');
                } catch (error) {
                  toast.error('Erro ao remover modelo');
                }
              }}
              onModelImageUpload={async (modelId, file) => {
                try {
                  const result = await uploadCevadorModelImage(modelId, file);
                  toast.success('Imagem atualizada!');
                  return result;
                } catch (error) {
                  toast.error('Erro ao enviar imagem');
                }
              }}
              saving={savingCevadorConfig}
            />
          )}

          {/* Gelatti */}
          {activeTab === 'gelatti' && (
            <GelattiSection onProductsUpdated={reloadProducts} />
          )}
          </div>
        </div>
      </div>

      {/* Category Modal */}
      <CategoryModal 
        isOpen={categoryModal.open}
        category={categoryModal.category}
        onClose={() => setCategoryModal({ open: false, category: null })}
        onSave={handleSaveCategory}
      />

      {/* Product Modal */}
      <ProductModal 
        isOpen={productModal.open}
        product={productModal.product}
        isDuplicate={productModal.isDuplicate}
        categories={categories}
        onClose={() => setProductModal({ open: false, product: null, isDuplicate: false })}
        onSave={handleSaveProduct}
        onUploadMainImage={handleProductImageUpload}
        onUploadAdditionalImage={handleProductAdditionalImageUpload}
        onDeleteAdditionalImage={handleDeleteProductAdditionalImage}
        onSaveImageAdjustment={handleSaveProductImageAdjustment}
        onSaveAdditionalImageAdjustment={handleSaveProductAdditionalImageAdjustment}
        onRefreshProduct={fetchAllData}
      />

      {/* Quote Detail Modal */}
      <QuoteDetailModal
        isOpen={quoteDetailModal.open}
        quote={quoteDetailModal.quote}
        onClose={() => setQuoteDetailModal({ open: false, quote: null })}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={passwordModal}
        userId={currentUser?.id}
        onClose={() => setPasswordModal(false)}
      />

      {/* New User Modal */}
      <NewUserModal
        isOpen={newUserModal}
        onClose={() => setNewUserModal(false)}
        onSuccess={fetchAllData}
      />

      {/* Permission Modal */}
      <Dialog open={permissionModal.open} onOpenChange={(open) => !open && setPermissionModal({ open: false, user: null, permissions: [] })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-600" />
              Permissões de {permissionModal.user?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-500">
              Selecione as seções que este usuário pode acessar. Se nenhuma for selecionada, o usuário terá acesso total (Admin).
            </p>
            
            <div className="space-y-2">
              {ALL_SECTIONS.map(section => (
                <label 
                  key={section.id}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={permissionModal.permissions.includes(section.id)}
                    onChange={() => togglePermission(section.id)}
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <section.icon className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-700">{section.name}</span>
                </label>
              ))}
            </div>
            
            {permissionModal.permissions.length === 0 && (
              <div className="p-3 bg-amber-50 rounded-lg flex items-center gap-2 text-amber-700 text-sm">
                <Key className="w-4 h-4" />
                <span>Sem seleção = Acesso total (Admin)</span>
              </div>
            )}
            
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setPermissionModal({ open: false, user: null, permissions: [] })}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                onClick={handleUpdatePermissions}
              >
                Salvar Permissões
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Modal */}
      <ContactModal
        isOpen={contactModal.open}
        contact={contactModal.contact}
        tags={crmTags}
        onClose={() => setContactModal({ open: false, contact: null })}
        onSave={async (data) => {
          try {
            if (contactModal.contact) {
              await updateCrmContact(contactModal.contact.id, data);
            } else {
              await createCrmContact(data);
            }
            await fetchAllData();
            setContactModal({ open: false, contact: null });
          } catch (error) {
            alert(error.response?.data?.detail || 'Erro ao salvar contato');
          }
        }}
      />

      {/* Contact Detail Modal */}
      <ContactDetailModal
        isOpen={contactDetailModal.open}
        contact={contactDetailModal.contact}
        currentUser={currentUser}
        onClose={() => setContactDetailModal({ open: false, contact: null })}
        onRefresh={fetchAllData}
      />

      {/* Banner Modal */}
      <BannerModal
        isOpen={bannerModal.open}
        banner={bannerModal.banner}
        products={products}
        onClose={() => setBannerModal({ open: false, banner: null })}
        onSave={async (data) => {
          try {
            if (bannerModal.banner) {
              await updatePromoBanner(bannerModal.banner.id, data);
            } else {
              await createPromoBanner(data);
            }
            setBannerModal({ open: false, banner: null });
            fetchAllData();
          } catch (error) {
            alert('Erro ao salvar banner');
          }
        }}
        onUploadImage={async (bannerId, file) => {
          await uploadPromoBannerImage(bannerId, file);
          fetchAllData();
        }}
      />
      
      {/* Toaster for in-app notifications */}
      <Toaster />
    </div>
  );
};

export default AdminPanel;
